/**
 * QUANTUM DAY — Admin Panel (Secure Version)
 *
 * ✅ TIDAK ADA credentials hardcoded di file ini:
 *    - Tidak ada username/password
 *    - Tidak ada Supabase URL atau key
 *    - Semua melalui /api/* Vercel Serverless Functions
 *
 * Environment Variables di Vercel (project admin):
 *   REACT_APP_API_BASE = https://admin.quantum-day.com
 */

import { useState, useEffect, useCallback, useRef } from "react";

// AUTH_API → always points to this admin domain (auth.js lives in quantum-day-admin)
const AUTH_API = "";
// DATA_API → ke quantum-day-main (articles, conferences, settings, dll)
const API = process.env.REACT_APP_API_BASE || "";
const SESSION_KEY = "qday_token_v3";
const getToken  = () => { try { return sessionStorage.getItem(SESSION_KEY)||""; } catch(_){return "";} };
const saveToken = t => { try { t ? sessionStorage.setItem(SESSION_KEY,t) : sessionStorage.removeItem(SESSION_KEY); } catch(_){} };

// ─── API LAYER — all credentials live server-side, never in the browser ──
const api = {
  h(token){ return {"Content-Type":"application/json","Authorization":`Bearer ${token}`}; },
  async login(username,password){
    const r=await fetch(`${AUTH_API}/api/auth`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({username,password})});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Login failed");}
    return (await r.json()).token;
  },
  async getArticles(token){
    const r=await fetch(`${API}/api/articles`,{headers:token?this.h(token):{}});
    if(!r.ok) throw new Error("Failed to load articles");
    return r.json();
  },
  async saveArticle(form,token){
    const r=await fetch(`${API}/api/articles`,{method:form.id?"PUT":"POST",headers:this.h(token),body:JSON.stringify(form)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Save failed");}
    return r.json();
  },
  async deleteArticle(id,token){
    const r=await fetch(`${API}/api/articles?id=${id}`,{method:"DELETE",headers:this.h(token)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Delete failed");}
  },
  async getConferences(){
    const r=await fetch(`${API}/api/conferences`);
    if(!r.ok) throw new Error("Failed to load conferences");
    return r.json();
  },
  async saveConference(form,token){
    const r=await fetch(`${API}/api/conferences`,{method:form.id?"PUT":"POST",headers:this.h(token),body:JSON.stringify(form)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Save failed");}
    return r.json();
  },
  async deleteConference(id,token){
    const r=await fetch(`${API}/api/conferences?id=${id}`,{method:"DELETE",headers:this.h(token)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Delete failed");}
  },
  async getSettings(token){
    const r=await fetch(`${API}/api/settings`,{headers:this.h(token)});
    if(!r.ok) throw new Error("Failed to load settings");
    return r.json();
  },
  async saveSettings(data,token){
    const r=await fetch(`${API}/api/settings`,{method:"POST",headers:this.h(token),body:JSON.stringify(data)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Save failed");}
  },
  async getSubscribers(token){
    const r=await fetch(`${API}/api/subscribers`,{headers:this.h(token)});
    if(!r.ok) throw new Error("Failed to load subscribers");
    return r.json();
  },
  async deleteSubscriber(id,token){
    const r=await fetch(`${API}/api/subscribers?id=${id}`,{method:"DELETE",headers:this.h(token)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Delete failed");}
  },
  async getSubmissions(token){
    const r=await fetch(`${API}/api/contact`,{headers:this.h(token)});
    if(!r.ok) throw new Error("Failed to load submissions");
    return r.json();
  },
  async markSubmission(id,status,token){
    const r=await fetch(`${API}/api/contact`,{method:"PATCH",headers:this.h(token),body:JSON.stringify({id,status})});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Update failed");}
  },
  async deleteSubmission(id,token){
    const r=await fetch(`${API}/api/contact?id=${id}`,{method:"DELETE",headers:this.h(token)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Delete failed");}
  },
  async getMenus(){
    const r=await fetch(`${API}/api/menus`);
    if(!r.ok) throw new Error("Failed to load menus");
    return r.json();
  },
  async saveMenus(data,token){
    const r=await fetch(`${API}/api/menus`,{method:"POST",headers:this.h(token),body:JSON.stringify(data)});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Save failed");}
  },
  async uploadImage(base64,filename,contentType,token){
    const r=await fetch(`${API}/api/upload`,{method:"POST",headers:this.h(token),body:JSON.stringify({base64,filename,contentType})});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Upload failed");}
    return r.json(); // { url, path }
  },
  async getImages(token){
    const r=await fetch(`${API}/api/settings`,{headers:this.h(token)});
    if(!r.ok) throw new Error("Failed to load images");
    const s=await r.json();
    return typeof s.site_images==="string"?JSON.parse(s.site_images):(s.site_images||{});
  },
  async saveImages(images,token){
    const r=await fetch(`${API}/api/settings`,{method:"POST",headers:this.h(token),body:JSON.stringify({site_images:JSON.stringify(images)})});
    if(!r.ok){const e=await r.json();throw new Error(e.error||"Save failed");}
  },
};

// ─── ICONS ───────────────────────────────────────────────────
const SVG={
  atom:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="1.5"/><ellipse cx="12" cy="12" rx="10" ry="4"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)"/></svg>,
  dash:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  bot:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 11V7"/><circle cx="12" cy="5" r="2"/><path d="M8 15h.01M12 15h.01M16 15h.01"/></svg>,
  chart:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>,
  article: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14,2 14,8 20,8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/></svg>,
  calendar:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  settings:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  logout:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  plus:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  save:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  x:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  shield:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  eye:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  check:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  alert:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  loader:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
  star:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  globe:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  mail:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  link:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  nav:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>,
  move:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>,
  link:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  send:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  clock:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  toggle:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>,
  image:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  upload:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>,
  lock:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
};
const Ico=({name,size=16,style={}})=>(<span style={{width:size,height:size,display:"inline-flex",flexShrink:0,...style}}>{SVG[name]}</span>);
const fmtViews=n=>{
  if(!n||n<1) return "—";
  if(n>=1000000) return (n/1000000).toFixed(1).replace(/[.]0$/,"")+"M";
  if(n>=1000)    return (n/1000).toFixed(1).replace(/[.]0$/,"")+"k";
  return String(n);
};

function calcReadTime(content="", excerpt="") {
  const text = (content + " " + excerpt).trim();
  if (!text) return "";
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const mins  = Math.ceil(words / 200); // 200 wpm for technical content
  return mins < 1 ? "1 min" : `${mins} min`;
}
const fmt=d=>d?new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):"—";

// ─── IMAGE UPLOADER COMPONENT (standalone - fixes re-render bug) ──────────────
function ImageUploader({ token, currentUrl="", onUploaded, label="Article Image", hint="Recommended: 1200×630px, JPG/PNG/WebP, max 5MB" }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;
    const maxMB = 5;
    if (file.size > maxMB * 1024 * 1024) { setError(`File too large (max ${maxMB}MB)`); return; }
    const allowed = ["image/jpeg","image/png","image/gif","image/webp","image/svg+xml"];
    if (!allowed.includes(file.type)) { setError("Only JPG, PNG, GIF, WebP, SVG allowed"); return; }
    setError(""); setUploading(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(",")[1];
      try {
        const result = await api.uploadImage(base64, file.name, file.type, token);
        onUploaded(result.url);
      } catch(err) {
        setError("Upload failed: " + err.message);
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); processFile(e.dataTransfer.files[0]); };
  const onDragOver = (e) => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);

  return (
    <div>
      <label style={{fontSize:11,fontWeight:700,letterSpacing:".5px",textTransform:"uppercase",color:"var(--t3)",display:"block",marginBottom:6}}>{label}</label>

      {currentUrl ? (
        <div className="img-preview">
          <img src={currentUrl} alt="Preview" onError={e=>e.target.style.display="none"}/>
          <button className="img-preview-remove" onClick={()=>onUploaded("")} title="Remove image">×</button>
          <div className="img-url-display">
            <a href={currentUrl} target="_blank" rel="noopener noreferrer">{currentUrl}</a>
            <button style={{background:"none",border:"none",cursor:"pointer",color:"var(--ac)",fontSize:11,whiteSpace:"nowrap"}} onClick={()=>fileRef.current?.click()}>Change</button>
          </div>
          <input type="file" ref={fileRef} style={{display:"none"}} accept="image/*" onChange={e=>processFile(e.target.files[0])}/>
        </div>
      ) : (
        <div className={`img-zone${drag?" drag":""}`} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
          <input type="file" accept="image/*" onChange={e=>processFile(e.target.files[0])}/>
          <div className="img-zone-icon"><Ico name="upload" size={28}/></div>
          <div className="img-zone-text">Drop image here or click to browse</div>
          <div className="img-zone-hint">{hint}</div>
        </div>
      )}

      {uploading && (
        <div className="img-uploading">
          <Ico name="loader" size={15} style={{animation:"spin 1s linear infinite"}}/>
          Uploading image to Supabase Storage…
        </div>
      )}
      {error && <div style={{fontSize:12,color:"var(--err)",marginTop:6,padding:"6px 10px",background:"var(--errb)",borderRadius:6}}>{error}</div>}
    </div>
  );
}

// ─── MEDIA MANAGER (module-level → stable identity, no remount) ───────────────
const IMG_SLOTS = [
  { group:"🏠 Homepage", desc:"Homepage images", items:[
    {key:"hero_banner",label:"Hero Banner",hint:"1600×600px — displayed at the top of homepage"},
    {key:"homepage_featured",label:"Featured Overlay",hint:"1200×630px — featured article background (optional)"},
  ]},
  { group:"📰 Article Pages", desc:"Default image for articles", items:[
    {key:"article_default_hero",label:"Default Article Hero",hint:"1200×630px — fallback image if article has no own image"},
    {key:"article_default_thumb",label:"Default Article Thumbnail",hint:"400×267px — thumbnail fallback for article list"},
  ]},
  { group:"🎤 Conferences", desc:"Conference page images", items:[
    {key:"conferences_banner",label:"Conferences Banner",hint:"1600×400px — conference page header"},
    {key:"conference_default",label:"Default Conference Image",hint:"800×400px — conference fallback image"},
  ]},
  { group:"🏢 Branding & Site", desc:"Identitas visual website", items:[
    {key:"site_logo",label:"Site Logo",hint:"PNG transparan, min 200×60px"},
    {key:"og_image",label:"Social Share Image (OG)",hint:"1200×630px — preview when link is shared on social media"},
    {key:"favicon",label:"Favicon",hint:"PNG 512×512px"},
  ]},
  { group:"📢 Newsletter & About", desc:"Image for newsletter and about page", items:[
    {key:"newsletter_bg",label:"Newsletter Background",hint:"1400×400px — background section newsletter"},
    {key:"about_hero",label:"About Page Banner",hint:"1600×500px — about page banner"},
  ]},
];

function MediaManager({ token, siteImages, articles, onSave, notify, setSection }) {
  const [imgs, setImgs] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync from parent when siteImages changes
  useEffect(() => { if (siteImages && typeof siteImages === "object") setImgs({...siteImages}); }, [siteImages]);

  const update = (key, url) => setImgs(p => ({...p, [key]: url}));
  const save = async () => {
    setSaving(true);
    try { await onSave(imgs); }
    finally { setSaving(false); }
  };

  return (
    <div className="fu">
      <div className="pt">Media &amp; Images</div>
      <div className="ps">Manage all website images: banner, default thumbnail, branding</div>

      <div style={{background:"var(--am)",border:"1px solid rgba(26,58,92,.12)",borderRadius:10,padding:"12px 16px",marginBottom:24,display:"flex",gap:10,alignItems:"flex-start"}}>
        <Ico name="image" size={16} style={{color:"var(--ac)",flexShrink:0,marginTop:2}}/>
        <div style={{fontSize:13,color:"var(--ac)",lineHeight:1.6}}>
          <strong>How it works:</strong> Upload image → saved to Supabase Storage → displayed on website.<br/>
          <strong>Format:</strong> JPG, PNG, WebP, SVG · <strong>Max:</strong> 5MB per file · Per-article images are set in the Edit Article form.
        </div>
      </div>

      {IMG_SLOTS.map(grp => (
        <div key={grp.group} className="setc" style={{marginBottom:16}}>
          <div className="sett" style={{fontSize:15,marginBottom:4}}>{grp.group}</div>
          <div className="setsub" style={{marginBottom:14}}>{grp.desc}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(270px,1fr))",gap:14}}>
            {grp.items.map(slot => (
              <div key={slot.key} style={{border:"1px solid var(--bd)",borderRadius:10,overflow:"hidden"}}>
                <div style={{padding:"10px 14px",background:"var(--bg)",borderBottom:"1px solid var(--bd)",fontSize:12,fontWeight:700,color:"var(--t2)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span>{slot.label}</span>
                  {imgs[slot.key] && <button style={{background:"none",border:"none",cursor:"pointer",color:"var(--err)",fontSize:11,fontWeight:600}} onClick={() => update(slot.key,"")}>✕ Remove</button>}
                </div>
                <div style={{padding:14}}>
                  {imgs[slot.key] ? (
                    <div style={{marginBottom:10}}>
                      <div style={{height:100,borderRadius:6,overflow:"hidden",marginBottom:8,background:"var(--bg2)"}}>
                        <img src={imgs[slot.key]} alt={slot.label} style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>
                      </div>
                      <div style={{fontSize:11,color:"var(--t3)",wordBreak:"break-all",padding:"4px 8px",background:"var(--bg)",borderRadius:4}}>
                        {imgs[slot.key].split("/").pop()}
                      </div>
                    </div>
                  ) : (
                    <div style={{height:100,background:"var(--bg2)",borderRadius:6,marginBottom:10,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--t3)",flexDirection:"column",gap:6}}>
                      <Ico name="image" size={22} style={{opacity:.3}}/>
                      <span style={{fontSize:11}}>No image yet</span>
                    </div>
                  )}
                  <ImageUploader token={token} currentUrl={imgs[slot.key]||""} label="" hint={slot.hint} onUploaded={url=>update(slot.key,url)}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Article images overview */}
      {articles.filter(a => a.image_url||a.thumbnail_url).length > 0 && (
        <div className="setc" style={{marginBottom:16}}>
          <div className="sett" style={{fontSize:15,marginBottom:4}}>📄 Article Images</div>
          <div className="setsub" style={{marginBottom:14}}>Articles that already have images. Click to edit.</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
            {articles.filter(a => a.image_url||a.thumbnail_url).slice(0,12).map(a => (
              <div key={a.id} style={{border:"1px solid var(--bd)",borderRadius:8,overflow:"hidden",cursor:"pointer"}} onClick={() => setSection("articles")}>
                <div style={{height:80,background:"var(--bg2)",overflow:"hidden"}}>
                  <img src={a.thumbnail_url||a.image_url} alt={a.title} style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>
                </div>
                <div style={{padding:"7px 9px"}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--t)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.title}</div>
                  <div style={{fontSize:10,color:"var(--t3)",marginTop:2}}>{a.image_url?"✓ Hero ":""}{a.thumbnail_url?"✓ Thumb":""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"flex-end",gap:10,padding:"14px 0",borderTop:"1px solid var(--bd)",background:"var(--bg)",position:"sticky",bottom:0}}>
        <button className="btn bse" onClick={() => setImgs({...siteImages})}>Reset</button>
        <button className="btn bp" onClick={save} disabled={saving}>
          {saving ? <><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Menyimpan…</> : <><Ico name="save" size={14}/>Save &amp; Publish</>}
        </button>
      </div>
    </div>
  );
}

// ─── NEWSLETTER ADMIN (module-level) ──────────────────────────────────────────
function NewsletterAdmin({ token, notify }) {
  const API_BASE = (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) || "";
  const h = { "Content-Type":"application/json", "Authorization":`Bearer ${token}` };

  const [cfg, setCfg] = useState({ newsletter_enabled:"false", newsletter_day:"1", newsletter_time:"08:00", newsletter_subject_prefix:"Quantum Day Weekly", newsletter_last_sent:"", verified_count:0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/newsletter`, { headers: h })
      .then(r => r.json())
      .then(d => { setCfg(p => ({...p,...d})); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/newsletter`, { method:"POST", headers:h, body:JSON.stringify({ action:"settings", ...cfg }) });
      if (!r.ok) throw new Error("Save failed");
      notify("Newsletter settings saved");
    } catch(e) { notify(e.message, "error"); }
    finally { setSaving(false); }
  };

  const sendNow = () => {
    setConfirm({
      type:"send",icon:"send",title:"Send Newsletter",
      msg:`Send to all ${cfg.verified_count||0} verified subscribers now?`,
      detail:"This week's articles will be compiled and sent immediately. This cannot be undone.",
      confirmLabel:"Send Now",
      onOk:async()=>{
        setConfirm(null);
        setSending(true); setSendResult(null);
    try {
      const r = await fetch(`${API_BASE}/api/newsletter`, { method:"POST", headers:h, body:JSON.stringify({ action:"send" }) });
        const d = await r.json();
        setSendResult(d);
        if (d.ok) notify(`Newsletter sent to ${d.sent} subscriber${d.failed ? ` (${d.failed} failed)` : ""}`);
        else notify("Sending failed", "error");
      } catch(e) { notify(e.message,"error"); }
      finally { setSending(false); }
      }
    });
  };

  const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

  if (loading) return <div className="fu" style={{textAlign:"center",padding:60,color:"var(--t3)"}}>Loading newsletter settings…</div>;

  return (
    <div className="fu">
      <div className="pt">Newsletter</div>
      <div className="ps">Manage weekly automated newsletter delivery to all verified subscribers</div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
        {[
          ["Subscriber Verified", cfg.verified_count||0, "check"],
          ["Status", cfg.newsletter_enabled==="true"?"Active":"Inactive", cfg.newsletter_enabled==="true"?"toggle":"x"],
          ["Last Sent", cfg.newsletter_last_sent ? new Date(cfg.newsletter_last_sent).toLocaleDateString("en-US",{day:"numeric",month:"long",year:"numeric"}) : "Never", "clock"],
        ].map(([l,v,ic]) => (
          <div key={l} style={{background:"var(--s)",border:"1px solid var(--bd)",borderRadius:12,padding:"18px 20px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}><Ico name={ic} size={16} style={{color:"var(--ac)"}}/><div style={{fontSize:11,fontWeight:700,letterSpacing:".5px",textTransform:"uppercase",color:"var(--t3)"}}>{l}</div></div>
            <div style={{fontSize:20,fontWeight:700,color:"var(--t)"}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Scheduler settings */}
      <div className="setc" style={{marginBottom:16}}>
        <div className="sett">⏰ Automatic Sending Schedule</div>
        <div className="setsub">Newsletter is sent automatically on schedule via Vercel Cron. Configure cron in vercel.json.</div>
        <div className="fg" style={{marginTop:14}}>
          <div className="fgr">
            <label className="fl">Status Newsletter</label>
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:4}}>
              <label className="fchk">
                <input type="checkbox" checked={cfg.newsletter_enabled==="true"}
                  onChange={e => setCfg(p=>({...p,newsletter_enabled:e.target.checked?"true":"false"}))}/>
                <span>Enable automatic sending</span>
              </label>
            </div>
          </div>
          <div className="fgr">
            <label className="fl">Sending day</label>
            <select className="fc2" value={cfg.newsletter_day} onChange={e=>setCfg(p=>({...p,newsletter_day:e.target.value}))}>
              {days.map((d,i) => <option key={i} value={String(i)}>{d}</option>)}
            </select>
            <span className="fhint">Day of the week to send newsletters</span>
          </div>
          <div className="fgr">
            <label className="fl">Time (UTC+7)</label>
            <input className="fc2" type="time" value={cfg.newsletter_time} onChange={e=>setCfg(p=>({...p,newsletter_time:e.target.value}))}/>
            <span className="fhint">Cron in Vercel runs in UTC — adjust for your timezone</span>
          </div>
          <div className="fgr full">
            <label className="fl">Prefix Subject Email</label>
            <input className="fc2" value={cfg.newsletter_subject_prefix} onChange={e=>setCfg(p=>({...p,newsletter_subject_prefix:e.target.value}))} placeholder="Quantum Day Weekly"/>
            <span className="fhint">Email subject = prefix + title of most popular article. Example: "Quantum Day Weekly: IBM Launches 1000-Qubit Chip"</span>
          </div>
        </div>
        <div style={{marginTop:20,padding:"14px 16px",background:"var(--am)",borderRadius:8,fontSize:13,color:"var(--ac)"}}>
          <strong>Setup Vercel Cron:</strong> Add to <code style={{background:"var(--am2)",padding:"1px 5px",borderRadius:3}}>vercel.json</code>:<br/>
          <code style={{display:"block",marginTop:8,background:"#0c1a2e",color:"#a8d8f0",padding:"10px 14px",borderRadius:6,fontSize:12}}>
            {`{"crons": [{"path": "/api/newsletter-cron", "schedule": "0 1 * * ${cfg.newsletter_day}"}]}`}
          </code>
          <span style={{fontSize:11,color:"var(--t3)",marginTop:6,display:"block"}}>Schedule <strong>0 1 * * {cfg.newsletter_day}</strong> = Every day {days[parseInt(cfg.newsletter_day)]} at 01:00 UTC (08:00 WIB)</span>
        </div>
        <div style={{marginTop:14,display:"flex",justifyContent:"flex-end"}}>
          <button className="btn bp" onClick={saveSettings} disabled={saving}>
            {saving ? <><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Menyimpan…</> : <><Ico name="save" size={14}/>Save Settings</>}
          </button>
        </div>
      </div>

      {/* Manual send */}
      <div className="setc" style={{marginBottom:16}}>
        <div className="sett">📤 Send Newsletter Now</div>
        <div className="setsub">Manually send this week's newsletter edition to all {cfg.verified_count||0} verified subscribers.</div>
        <div style={{background:"var(--warnb)",border:"1px solid rgba(183,119,13,.2)",borderRadius:8,padding:"12px 16px",marginTop:14,marginBottom:16,fontSize:13,color:"var(--warn)"}}>
          ⚠️ Newsletter will contain <strong>articles from the last 7 days</strong> (or the latest article if there is nothing new this week). Sending process may take several minutes depending on subscriber count.
        </div>

        {sendResult && (
          <div style={{background:sendResult.ok?"var(--okb)":"var(--errb)",border:`1px solid ${sendResult.ok?"var(--okbd)":"var(--errbd)"}`,borderRadius:8,padding:"14px 16px",marginBottom:16,fontSize:14,color:sendResult.ok?"var(--ok)":"var(--err)"}}>
            {sendResult.ok
              ? `✓ Newsletter successfully sent to ${sendResult.sent} subscriber.${sendResult.failed ? ` ${sendResult.failed} failed.` : ""} ${sendResult.skipped ? "("+sendResult.skipped+")" : ""}${sendResult.lastError ? ` Error: ${sendResult.lastError}` : ""}`
              : "Sending failed: " + JSON.stringify(sendResult)}
          </div>
        )}

        <button className="btn bp" onClick={sendNow} disabled={sending||cfg.verified_count===0} style={{gap:10}}>
          {sending
            ? <><Ico name="loader" size={15} style={{animation:"spin 1s linear infinite"}}/>Sending newsletter…</>
            : <><Ico name="send" size={15}/>Send Newsletter Now ({cfg.verified_count||0} subscriber)</>}
        </button>
        {(cfg.verified_count||0) === 0 && <div style={{fontSize:12,color:"var(--t3)",marginTop:8}}>No verified subscribers yet.</div>}
      </div>

      {/* Email template preview */}
      <div className="setc">
        <div className="sett">📧 Email Newsletter Format</div>
        <div className="setsub">Preview of the email layout sent to subscribers.</div>
        <div style={{marginTop:14,border:"1px solid var(--bd)",borderRadius:8,overflow:"hidden",maxWidth:540}}>
          <div style={{background:"#1e3a5f",padding:"18px 24px",textAlign:"center"}}>
            <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color:"#fff"}}>⚛ Quantum Day</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,.55)",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:4}}>Weekly Edition</div>
          </div>
          <div style={{padding:"20px 24px",background:"#f8fafc"}}>
            {[["🔬 FEATURED ARTICLE","Title of this week's featured or most popular article","Article excerpt summary...","Read More →","#1e3a5f"],["📰 LATEST NEWS","6 latest articles in compact list format with small thumbnails","","",""],["📢 CTA","Button to invite visitors to the website","","",""]].map(([h,t,e,btn,bc]) => (
              <div key={h} style={{marginBottom:16,padding:"14px",background:"#fff",borderRadius:6,border:"1px solid #e2e8f0"}}>
                <div style={{fontSize:9,fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:"#1e3a5f",marginBottom:8,paddingBottom:6,borderBottom:"2px solid #1e3a5f"}}>{h}</div>
                <div style={{fontSize:13,fontWeight:600,color:"#1e3a5f",marginBottom:4}}>{t}</div>
                {e && <div style={{fontSize:12,color:"#64748b",marginBottom:8}}>{e}</div>}
                {btn && <div style={{display:"inline-block",background:bc||"#1e3a5f",color:"#fff",padding:"6px 16px",borderRadius:4,fontSize:12,fontWeight:700}}>{btn}</div>}
              </div>
            ))}
            <div style={{textAlign:"center",padding:"12px",fontSize:11,color:"#94a3b8"}}>Footer with unsubscribe link · © {new Date().getFullYear()} Quantum Day</div>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── NEWS BOT PANEL ─────────────────────────────────────────── */
const RSS_SOURCES_INFO = [
  {name:"arXiv Quantum Physics",     url:"arxiv.org/rss/quant-ph",           category:"Research"},
  {name:"Phys.org Quantum",          url:"phys.org",                          category:"Research"},
  {name:"ScienceDaily Quantum",      url:"sciencedaily.com",                  category:"Technology"},
  {name:"Nature Quantum Information",url:"nature.com",                        category:"Research"},
  {name:"New Scientist",             url:"newscientist.com",                   category:"Technology"},
];

const CRON_PRESETS = [
  {label:"Every 6 hours",   cron:"0 */6 * * *",  desc:"4× per day — recommended"},
  {label:"Every 12 hours",  cron:"0 */12 * * *", desc:"2× per day"},
  {label:"Once daily 8am",  cron:"0 8 * * *",    desc:"1× per day, 08:00 UTC"},
  {label:"Twice daily",     cron:"0 8,20 * * *", desc:"08:00 & 20:00 UTC"},
  {label:"Every 3 hours",   cron:"0 */3 * * *",  desc:"8× per day — high volume"},
];

function NewsBotPanel({ token, notify, setConfirm }) {
  // ── Config state (loaded from Supabase site_settings) ──────
  const [cfg, setCfg] = useState({
    maxPerRun:  5,
    pubStatus:  "draft",
    schedIdx:   4, // default: every 3 hours
  });
  const [sources,    setSources]    = useState(RSS_SOURCES_INFO.map((s,i)=>({...s,id:i+1,enabled:true})));
  const [savingCfg,  setSavingCfg]  = useState(false);
  const [loadingCfg, setLoadingCfg] = useState(true);

  // ── Logs state ─────────────────────────────────────────────
  const [logs,     setLogs]     = useState([]);
  const [loadLogs, setLoadLogs] = useState(false);
  const [lastRun,  setLastRun]  = useState(null);
  const [stats,    setStats]    = useState({published:0,skipped:0,errors:0});

  // ── RSS edit state ─────────────────────────────────────────
  const [editingSource, setEditingSource] = useState(null); // null | {id,name,url,category,enabled} | "new"
  const [srcForm,       setSrcForm]       = useState({name:"",url:"",category:"Research",enabled:true});

  // ── Load config & logs from Supabase ───────────────────────
  useEffect(()=>{
    if(!token) return;
    // Load config
    fetch(`${API}/api/settings`,{headers:{"Authorization":`Bearer ${token}`}})
      .then(r=>r.ok?r.json():null)
      .then(data=>{
        if(!data) return;
        const saved = data.newsbot_config ? JSON.parse(data.newsbot_config) : null;
        if(saved){
          if(saved.maxPerRun)  setCfg(p=>({...p,maxPerRun:saved.maxPerRun}));
          if(saved.pubStatus)  setCfg(p=>({...p,pubStatus:saved.pubStatus}));
          if(saved.schedIdx!=null) setCfg(p=>({...p,schedIdx:saved.schedIdx}));
          if(saved.sources)    setSources(saved.sources);
        }
      })
      .catch(()=>{})
      .finally(()=>setLoadingCfg(false));
    // Load logs
    setLoadLogs(true);
    fetch(`${API}/api/newsbot-logs`,{headers:{"Authorization":`Bearer ${token}`}})
      .then(r=>r.ok?r.json():null)
      .then(data=>{
        if(!data||!Array.isArray(data)) return;
        const recent = data.slice(0,20);
        setLogs(recent);
        if(recent.length>0){
          const last = recent[0];
          setLastRun(last.ran_at);
          setStats({published:last.published||0,skipped:last.skipped||0,errors:last.errors||0});
        }
      })
      .catch(()=>{})
      .finally(()=>setLoadLogs(false));
  },[token]);

  // ── Save config to Supabase ─────────────────────────────────
  async function saveConfig(){
    setSavingCfg(true);
    try{
      const payload = { newsbot_config: JSON.stringify({...cfg, sources}) };
      const r = await fetch(`${API}/api/settings`,{
        method:"POST",
        headers:{"Authorization":`Bearer ${token}`,"Content-Type":"application/json"},
        body: JSON.stringify(payload),
      });
      if(!r.ok) throw new Error("Save failed");
      notify("✅ Bot configuration saved","success");
    }catch(e){
      notify("❌ Failed to save: "+e.message,"error");
    }finally{
      setSavingCfg(false);
    }
  }

  // ── RSS Source CRUD ─────────────────────────────────────────
  function openNewSource(){
    setSrcForm({name:"",url:"",category:"Research",enabled:true});
    setEditingSource("new");
  }
  function openEditSource(s){
    setSrcForm({name:s.name,url:s.url,category:s.category,enabled:s.enabled});
    setEditingSource(s);
  }
  function cancelEdit(){ setEditingSource(null); }
  function saveSource(){
    if(!srcForm.name.trim()||!srcForm.url.trim()){
      notify("Name and URL are required","error"); return;
    }
    // Normalize URL — strip https://
    const url = srcForm.url.replace(/^https?:\/\//,"").replace(/\/$/,"");
    if(editingSource==="new"){
      const newId = Date.now();
      setSources(p=>[...p,{...srcForm,url,id:newId}]);
      notify("✅ RSS source added — click Save to apply","success");
    } else {
      setSources(p=>p.map(s=>s.id===editingSource.id?{...s,...srcForm,url}:s));
      notify("✅ RSS source updated — click Save to apply","success");
    }
    setEditingSource(null);
  }
  function toggleSource(id){
    setSources(p=>p.map(s=>s.id===id?{...s,enabled:!s.enabled}:s));
  }
  function deleteSource(id){
    setConfirm({
      msg:"Delete this RSS source?",
      onOk:()=>{ setSources(p=>p.filter(s=>s.id!==id)); notify("Source removed — click Save to apply","success"); }
    });
  }

  const activeSources = sources.filter(s=>s.enabled).length;
  const isRunning = lastRun && (Date.now()-new Date(lastRun).getTime()) < 120000;

  return(
<div className="fu">
  <div className="pt">News Bot</div>
  <div className="ps">Autonomous news fetcher · AI rewriter · Auto-publisher via GitHub Actions</div>

  {/* Status bar */}
  <div className="nb-status">
    <div className={`nb-dot${isRunning?" running":" on"}`}/>
    <span className="nb-status-txt">Active — Auto-publishing via GitHub Actions</span>
    <span className="nb-status-sub">
      {lastRun?`Last run: ${new Date(lastRun).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit",hour12:true})}`:"Never run yet"}
    </span>
    <button onClick={saveConfig} disabled={savingCfg||loadingCfg}
      style={{marginLeft:"auto",padding:"6px 16px",background:"var(--ac)",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",opacity:savingCfg?0.6:1}}>
      {savingCfg?"Saving…":"💾 Save Changes"}
    </button>
  </div>

  <div className="dg2" style={{marginBottom:18}}>

    {/* ── Left: Configuration ── */}
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div className="tc">
        <div className="th"><span className="tht">⚙️ Configuration</span></div>
        <div style={{padding:"18px 20px",display:"flex",flexDirection:"column",gap:18}}>

          {/* Max articles per run */}
          <div>
            <label className="fl">Max Articles per Run</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {[1,2,3,4,5,6,7,8,10].map(n=>(
                <button key={n} onClick={()=>setCfg(p=>({...p,maxPerRun:n}))}
                  style={{padding:"6px 14px",borderRadius:7,border:"1px solid",fontSize:13,fontWeight:600,cursor:"pointer",
                    borderColor:cfg.maxPerRun===n?"var(--ac)":"var(--bd)",
                    background:cfg.maxPerRun===n?"var(--ac)":"var(--bg)",
                    color:cfg.maxPerRun===n?"#fff":"var(--t2)"}}>
                  {n}
                </button>
              ))}
            </div>
            <span className="fhint">Saved to Supabase — GitHub Actions reads this value automatically on each run. No code changes needed.</span>
          </div>

          {/* Publish status */}
          <div>
            <label className="fl">New Articles Published As</label>
            <div style={{display:"flex",gap:8}}>
              {[["published","✅ Published","#22c55e"],["draft","✏️ Draft","var(--t4)"]].map(([val,lbl,col])=>(
                <button key={val} onClick={()=>setCfg(p=>({...p,pubStatus:val}))}
                  style={{padding:"8px 18px",borderRadius:7,border:"1px solid",fontSize:13,fontWeight:600,cursor:"pointer",
                    borderColor:cfg.pubStatus===val?col:"var(--bd)",
                    background:cfg.pubStatus===val?col:"var(--bg)",
                    color:cfg.pubStatus===val?"#fff":"var(--t2)"}}>
                  {lbl}
                </button>
              ))}
            </div>
            <span className="fhint">Saved to Supabase — GitHub Actions reads this value automatically on each run. No code changes needed.</span>
          </div>

          {/* Schedule — read-only info */}
          <div>
            <label className="fl">Auto-run Schedule</label>
            <div style={{background:"var(--bg2)",border:"1px solid var(--bd)",borderRadius:8,padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:"#22c55e",flexShrink:0,boxShadow:"0 0 0 3px rgba(34,197,94,.2)"}}/>
                <span style={{fontSize:13,fontWeight:700,color:"var(--t)"}}>Every 3 hours</span>
                <code style={{fontSize:11,color:"var(--t4)",background:"var(--bg3)",padding:"2px 7px",borderRadius:4,marginLeft:"auto"}}>0 */3 * * *</code>
              </div>
              <div style={{fontSize:11,color:"var(--t3)",lineHeight:1.5,paddingLeft:20}}>
                8× per day — runs automatically via GitHub Actions
              </div>
            </div>
            <div style={{marginTop:8,padding:"10px 12px",background:"rgba(245,158,11,.07)",border:"1px solid rgba(245,158,11,.2)",borderRadius:7,fontSize:11,color:"#b45309",lineHeight:1.6}}>
              <strong>Read-only.</strong> To change the schedule, edit <code style={{fontSize:10}}>.github/workflows/news-bot.yml</code> in your GitHub repository and update the <code style={{fontSize:10}}>schedule:</code> value. Common options:
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 12px",marginTop:6}}>
                {[["Every 3 hours","0 */3 * * *"],["Every 6 hours","0 */6 * * *"],["Twice daily","0 8,20 * * *"],["Once daily 8am","0 8 * * *"]].map(([lbl,cron])=>(
                  <div key={cron} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span>{lbl}</span>
                    <code style={{fontSize:10,background:"var(--bg3)",padding:"1px 5px",borderRadius:3}}>{cron}</code>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      {/* Stat cards */}
      <div className="nb-grid">
        <div className="nb-card">
          <div className="nb-card-label">📝 Published</div>
          <div className="nb-card-val" style={{color:"#22c55e"}}>{stats.published}</div>
          <div className="nb-card-sub">last run</div>
        </div>
        <div className="nb-card">
          <div className="nb-card-label">⏭ Skipped</div>
          <div className="nb-card-val">{stats.skipped}</div>
          <div className="nb-card-sub">duplicates</div>
        </div>
      </div>
      </div>

      {/* ── Read-only: Hardcoded Parameters ── */}
      <div className="tc">
        <div className="th"><span className="tht">🔒 Read-Only Parameters</span></div>
        <div style={{padding:"14px 18px",display:"flex",flexDirection:"column",gap:0}}>
          <div style={{fontSize:11,color:"var(--t4)",marginBottom:12,lineHeight:1.5}}>
            These parameters are hardcoded in <code style={{fontSize:10}}>.github/scripts/news-bot.js</code>. To change them, edit the file directly in your GitHub repository and commit the changes.
          </div>
          {[
            {
              label:"AI Model",
              value:"gemini-2.5-flash-lite",
              how:"Edit the GEMINI_MODEL constant in .github/scripts/news-bot.js",
              icon:"🤖"
            },
            {
              label:"Storage Bucket",
              value:"media / bot-heroes/",
              how:"Edit the STORAGE_BUCKET and STORAGE_SUBFOLDER constants in .github/scripts/news-bot.js",
              icon:"🗂"
            },
            {
              label:"Author Name",
              value:"Quantum Day Staff",
              how:"Update the AUTHOR_NAME secret in GitHub repository Settings → Secrets and variables → Actions",
              icon:"✍️"
            },
            {
              label:"Article Age Filter",
              value:"Last 48 hours",
              how:"Edit the cutoff constant (48 * 3600000) in the main() function of .github/scripts/news-bot.js",
              icon:"⏱"
            },
            {
              label:"Image Sources",
              value:"Unsplash → Wikimedia → SVG fallback",
              how:"Add UNSPLASH_ACCESS_KEY to GitHub Secrets and Vercel Environment Variables to enable Unsplash",
              icon:"🖼"
            },
          ].map(({label,value,how,icon})=>(
            <div key={label} style={{display:"flex",flexDirection:"column",gap:3,padding:"10px 0",borderBottom:"1px solid var(--bd2)"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:13}}>{icon}</span>
                <span style={{fontSize:12,fontWeight:700,color:"var(--t2)",flex:1}}>{label}</span>
                <code style={{fontSize:11,background:"var(--bg2)",border:"1px solid var(--bd)",padding:"2px 8px",borderRadius:5,color:"var(--t)",fontWeight:600}}>{value}</code>
              </div>
              <div style={{fontSize:11,color:"var(--t4)",paddingLeft:21,lineHeight:1.5}}>
                To change: {how}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ── Right: RSS Sources ── */}
    <div className="tc">
      <div className="th">
        <span className="tht">📡 RSS Sources ({activeSources}/{sources.length} active)</span>
        <button onClick={openNewSource}
          style={{padding:"5px 12px",background:"var(--ac)",color:"#fff",border:"none",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer"}}>
          + Add Source
        </button>
      </div>
      <div style={{padding:"14px 18px"}}>

        {/* Edit / Add form */}
        {editingSource!==null && (
          <div style={{background:"var(--bg2)",border:"1px solid var(--ac)",borderRadius:10,padding:"16px",marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:13,color:"var(--t)",marginBottom:12}}>
              {editingSource==="new"?"➕ Add New RSS Source":"✏️ Edit RSS Source"}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div>
                <label className="fl">Source Name</label>
                <input className="fc2" placeholder="e.g. MIT News Quantum" value={srcForm.name}
                  onChange={e=>setSrcForm(p=>({...p,name:e.target.value}))}
                  style={{width:"100%"}}/>
              </div>
              <div>
                <label className="fl">RSS Feed URL</label>
                <input className="fc2" placeholder="https://example.com/feed.xml" value={srcForm.url}
                  onChange={e=>setSrcForm(p=>({...p,url:e.target.value}))}
                  style={{width:"100%"}}/>
              </div>
              <div>
                <label className="fl">Category</label>
                <select className="fc2" value={srcForm.category} onChange={e=>setSrcForm(p=>({...p,category:e.target.value}))}>
                  {["Research","Technology","Security","Business","Policy"].map(c=>(
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={saveSource}
                  style={{padding:"7px 18px",background:"var(--ac)",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  {editingSource==="new"?"Add Source":"Save Changes"}
                </button>
                <button onClick={cancelEdit}
                  style={{padding:"7px 14px",background:"var(--bg)",color:"var(--t2)",border:"1px solid var(--bd)",borderRadius:7,fontSize:13,cursor:"pointer"}}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Source list */}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {sources.map(s=>(
            <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:8,
              border:"1px solid var(--bd)",background:s.enabled?"var(--bg)":"var(--bg2)",opacity:s.enabled?1:0.6}}>
              {/* Toggle active dot */}
              <div onClick={()=>toggleSource(s.id)}
                style={{width:10,height:10,borderRadius:"50%",flexShrink:0,cursor:"pointer",
                  background:s.enabled?"#22c55e":"var(--t4)",
                  boxShadow:s.enabled?"0 0 0 3px rgba(34,197,94,.2)":"none"}}
                title={s.enabled?"Click to disable":"Click to enable"}/>
              {/* Info */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13,color:"var(--t)",lineHeight:1.2}}>{s.name}</div>
                <div style={{fontSize:11,color:"var(--t4)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {s.url} · <span style={{color:"var(--ac)",fontWeight:600}}>{s.category}</span>
                </div>
              </div>
              {/* Actions */}
              <div style={{display:"flex",gap:4,flexShrink:0}}>
                <button onClick={()=>openEditSource(s)} title="Edit"
                  style={{padding:"4px 8px",background:"none",border:"1px solid var(--bd)",borderRadius:5,cursor:"pointer",fontSize:12,color:"var(--t3)"}}>
                  ✏️
                </button>
                <button onClick={()=>deleteSource(s.id)} title="Delete"
                  style={{padding:"4px 8px",background:"none",border:"1px solid rgba(192,57,43,.3)",borderRadius:5,cursor:"pointer",fontSize:12,color:"var(--err)"}}>
                  🗑
                </button>
              </div>
            </div>
          ))}
          {sources.length===0 && (
            <div style={{textAlign:"center",padding:"20px",color:"var(--t4)",fontSize:13}}>
              No RSS sources. Click "+ Add Source" to add one.
            </div>
          )}
        </div>

        <div style={{marginTop:12,padding:"10px 12px",background:"var(--bg2)",borderRadius:8,fontSize:11,color:"var(--t4)",lineHeight:1.6}}>
          💡 <strong>Tip:</strong> After adding or editing sources, click <strong>Save Changes</strong>. The GitHub Actions script will read this configuration from Supabase on its next run.
        </div>
      </div>
    </div>
  </div>

  {/* Run History */}
  <div className="tc">
    <div className="th">
      <span className="tht">📋 Run History</span>
      <span style={{fontSize:11,color:"var(--t4)"}}>{logs.length} recent runs</span>
    </div>
    <div style={{padding:"14px 18px"}}>
      {loadLogs && <div style={{color:"var(--t4)",fontSize:13}}>Loading logs…</div>}
      {!loadLogs && logs.length===0 && (
        <div className="nb-log">
          <span className="nb-log-empty">No run history yet. The bot runs automatically via GitHub Actions based on the configured schedule.</span>
        </div>
      )}
      {!loadLogs && logs.length>0 && logs.map((l,i)=>(
        <div key={i} style={{marginBottom:12}}>
          <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:11,fontWeight:700,
              color:l.status==="success"?"#22c55e":l.status==="failed"?"var(--err)":"var(--t4)",
              background:l.status==="success"?"rgba(34,197,94,.1)":l.status==="failed"?"var(--errb)":"var(--bg2)",
              padding:"2px 8px",borderRadius:10}}>
              {l.status==="success"?"✅ Success":l.status==="failed"?"❌ Failed":"ℹ️ No new articles"}
            </span>
            <span style={{fontSize:11,color:"var(--t4)"}}>
              {new Date(l.ran_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit",hour12:true})}
            </span>
            <span style={{fontSize:11,color:"var(--t4)",marginLeft:"auto"}}>
              {l.published}↑ {l.skipped}↷ {l.errors}✗ {l.duration_sec}s
            </span>
          </div>
          {i===0 && l.log && <div className="nb-log">{l.log}</div>}
        </div>
      ))}
    </div>
  </div>
</div>
  );
}

/* ── ANALYTICS DASHBOARD ─────────────────────────────────────── */
function AnalyticsPanel({ articles, confs, subscribers, setSection, openArt }) {
  const [period, setPeriod] = useState("30");  // days

  // ── Derived data ────────────────────────────────────────────
  const pub = articles.filter(a => !a.status || a.status === "published");
  const now  = new Date();
  const daysAgo = d => new Date(now - d * 86400000);

  // Articles published in period
  const inPeriod = pub.filter(a => {
    if (!a.published_at) return false;
    return new Date(a.published_at) >= daysAgo(parseInt(period));
  });

  // Total views
  const totalViews   = pub.reduce((s, a) => s + (a.views || 0), 0);
  const periodViews  = inPeriod.reduce((s, a) => s + (a.views || 0), 0);
  const avgViews     = pub.length ? Math.round(totalViews / pub.length) : 0;

  // Top articles by views
  const topByViews = [...pub].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 8);
  const maxViews   = topByViews[0]?.views || 1;

  // Category breakdown
  const catMap = {};
  pub.forEach(a => { catMap[a.category] = (catMap[a.category] || 0) + 1; });
  const catData = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
  const CAT_COLORS_MAP = {
    Research:"#1d4ed8", Technology:"#7c3aed", Security:"#b45309",
    Business:"#065f46", Policy:"#be123c", Conference:"#0369a1",
    Cryptocurrency:"#92400e",
  };
  const catColor = c => CAT_COLORS_MAP[c] || "#64748b";

  // Tag breakdown
  const tagMap = {};
  pub.forEach(a => { if (a.tag) tagMap[a.tag] = (tagMap[a.tag] || 0) + 1; });
  const tagData = Object.entries(tagMap).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxTag  = tagData[0]?.[1] || 1;

  // Monthly publish cadence — last 12 months
  const monthBuckets = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
    return {
      label: d.toLocaleDateString("en-US", { month: "short" }),
      key:   `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      count: 0,
    };
  });
  pub.forEach(a => {
    if (!a.published_at) return;
    const k = a.published_at.slice(0, 7);
    const b = monthBuckets.find(m => m.key === k);
    if (b) b.count++;
  });
  const maxMonth = Math.max(...monthBuckets.map(m => m.count), 1);

  // Views sparkline — top 12 articles sorted by publish date
  const sparkArts = [...pub]
    .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
    .slice(0, 12).reverse();
  const maxSpark = Math.max(...sparkArts.map(a => a.views || 0), 1);

  // Subscriber growth — by month (last 6 months)
  const subBuckets = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { label: d.toLocaleDateString("en-US", { month: "short" }), key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, count: 0 };
  });
  subscribers.forEach(s => {
    if (!s.created_at) return;
    const k = s.created_at.slice(0, 7);
    const b = subBuckets.find(m => m.key === k);
    if (b) b.count++;
  });
  const maxSub = Math.max(...subBuckets.map(b => b.count), 1);

  // Verified vs pending subscribers
  const verifiedSubs = subscribers.filter(s => s.verified).length;
  const pendingSubs  = subscribers.length - verifiedSubs;

  // Author breakdown
  const authorMap = {};
  pub.forEach(a => { if (a.author) authorMap[a.author] = (authorMap[a.author] || 0) + 1; });
  const authorData = Object.entries(authorMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxAuthor  = authorData[0]?.[1] || 1;

  // ── KPI cards ───────────────────────────────────────────────
  const kpis = [
    { label: "Total Articles",    val: pub.length,                 sub: `${articles.filter(a=>a.status==="draft").length} drafts`,             accent: "#1d4ed8", icon: "📝" },
    { label: "Total Views",       val: fmtViews(totalViews),       sub: `avg ${fmtViews(avgViews)} per article`,                               accent: "#7c3aed", icon: "👁" },
    { label: "Subscribers",       val: subscribers.length,         sub: `${verifiedSubs} verified · ${pendingSubs} pending`,                   accent: "#065f46", icon: "✉️" },
    { label: `Published (${period}d)`, val: inPeriod.length,      sub: `${fmtViews(periodViews)} views in period`,                             accent: "#b45309", icon: "📅" },
  ];

  return (
    <div className="fu">
      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div className="pt">Analytics</div>
          <div className="ps" style={{margin:0}}>Content performance & audience insights</div>
        </div>
        <div className="an-tabs">
          {[["7","7 days"],["30","30 days"],["90","90 days"],["365","1 year"]].map(([v,l])=>(
            <button key={v} className={`an-tab${period===v?" on":""}`} onClick={()=>setPeriod(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="an-grid">
        {kpis.map((k,i)=>(
          <div key={i} className="an-card">
            <div className="an-card-accent" style={{background:k.accent}}/>
            <div className="an-card-label">{k.icon} {k.label}</div>
            <div className="an-card-val">{k.val}</div>
            <div className="an-card-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Row 1: Publish cadence + Views sparkline */}
      <div className="an-row" style={{marginBottom:16}}>
        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">📅 Monthly Publish Cadence</span>
            <span className="an-box-sub">Last 12 months</span>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",gap:5,height:80}}>
            {monthBuckets.map((m,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                <div style={{width:"100%",background:"var(--bg2)",borderRadius:"3px 3px 0 0",display:"flex",alignItems:"flex-end",height:56}}>
                  <div style={{width:"100%",background:"#1d4ed8",borderRadius:"3px 3px 0 0",opacity:.75,
                    height:`${Math.max(4,(m.count/maxMonth)*56)}px`,transition:"height .5s ease"}}/>
                </div>
                <span style={{fontSize:9,color:"var(--t4)",letterSpacing:"-.2px"}}>{m.label}</span>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,fontSize:12,color:"var(--t4)"}}>
            Total published: <strong style={{color:"var(--t)"}}>{pub.length}</strong> articles ·
            Peak: <strong style={{color:"var(--t)"}}>{Math.max(...monthBuckets.map(m=>m.count))}</strong>/mo
          </div>
        </div>

        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">👁 Views per Article</span>
            <span className="an-box-sub">Most recent 12</span>
          </div>
          <div className="an-spark">
            {sparkArts.map((a,i)=>(
              <div key={a.id} className="an-spark-bar"
                style={{background:"#7c3aed",height:`${Math.max(4,((a.views||0)/maxSpark)*48)}px`}}
                title={`${a.title.slice(0,40)}: ${fmtViews(a.views)} views`}
                onClick={()=>{setSection("articles");openArt(a);}}
              />
            ))}
          </div>
          <div style={{marginTop:10,fontSize:12,color:"var(--t4)"}}>
            Best: <strong style={{color:"var(--t)"}}>{fmtViews(topByViews[0]?.views||0)}</strong> views ·
            Avg: <strong style={{color:"var(--t)"}}>{fmtViews(avgViews)}</strong>
          </div>
        </div>
      </div>

      {/* Row 2: Top articles by views */}
      <div className="an-row w1" style={{marginBottom:16}}>
        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">🏆 Top Articles by Views</span>
            <span className="an-box-sub">All time</span>
          </div>
          {topByViews.length === 0
            ? <div className="an-empty">No articles yet</div>
            : <div className="an-bar-wrap">
                {topByViews.map((a,i)=>(
                  <div key={a.id} className="an-bar-row">
                    <span style={{fontSize:10,fontWeight:700,color:"var(--t4)",width:16,textAlign:"right",flexShrink:0}}>{i+1}</span>
                    <span className="an-bar-label" onClick={()=>{setSection("articles");openArt(a);}} title={a.title}>{a.title}</span>
                    <div className="an-bar-track">
                      <div className="an-bar-fill"
                        style={{width:`${((a.views||0)/maxViews)*100}%`,
                          background:i===0?"#1d4ed8":i===1?"#7c3aed":i===2?"#0369a1":"#64748b"}}/>
                    </div>
                    <span className="an-bar-val">{fmtViews(a.views)}</span>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>

      {/* Row 3: Category + Tags */}
      <div className="an-row" style={{marginBottom:16}}>
        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">📂 Articles by Category</span>
            <span className="an-box-sub">{pub.length} total</span>
          </div>
          {catData.length === 0 ? <div className="an-empty">No data</div> :
            <div className="an-legend">
              {catData.map(([cat, count]) => (
                <div key={cat} className="an-legend-row">
                  <div className="an-legend-dot" style={{background:catColor(cat)}}/>
                  <span className="an-legend-name">{cat}</span>
                  <div className="an-bar-track" style={{maxWidth:100}}>
                    <div className="an-bar-fill" style={{width:`${(count/pub.length)*100}%`,background:catColor(cat)}}/>
                  </div>
                  <span className="an-legend-pct">{Math.round((count/pub.length)*100)}%</span>
                  <span className="an-legend-count">{count}</span>
                </div>
              ))}
            </div>
          }
        </div>

        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">🏷 Top Tags</span>
            <span className="an-box-sub">{tagData.length} unique tags</span>
          </div>
          {tagData.length === 0 ? <div className="an-empty">No tags assigned yet</div> :
            <div className="an-bar-wrap">
              {tagData.map(([tag, count]) => (
                <div key={tag} className="an-bar-row">
                  <span className="an-bar-label">#{tag}</span>
                  <div className="an-bar-track">
                    <div className="an-bar-fill" style={{width:`${(count/maxTag)*100}%`,background:"#b45309"}}/>
                  </div>
                  <span className="an-bar-val">{count}</span>
                </div>
              ))}
            </div>
          }
        </div>
      </div>

      {/* Row 4: Subscriber growth + Authors */}
      <div className="an-row">
        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">📈 Subscriber Growth</span>
            <span className="an-box-sub">Last 6 months · new signups</span>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",gap:6,height:70}}>
            {subBuckets.map((b,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                <div style={{width:"100%",background:"var(--bg2)",borderRadius:"3px 3px 0 0",display:"flex",alignItems:"flex-end",height:52}}>
                  <div style={{width:"100%",background:"#065f46",borderRadius:"3px 3px 0 0",opacity:.8,
                    height:`${Math.max(b.count>0?5:0,(b.count/maxSub)*52)}px`,transition:"height .5s ease"}}/>
                </div>
                <span style={{fontSize:9,color:"var(--t4)"}}>{b.label}</span>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,display:"flex",gap:20,fontSize:12,color:"var(--t4)"}}>
            <span>Total: <strong style={{color:"var(--t)"}}>{subscribers.length}</strong></span>
            <span>Verified: <strong style={{color:"#065f46"}}>{verifiedSubs}</strong></span>
            <span>Pending: <strong style={{color:"var(--t)"}}>{pendingSubs}</strong></span>
          </div>
        </div>

        <div className="an-box">
          <div className="an-box-hd">
            <span className="an-box-title">✍️ Top Authors</span>
            <span className="an-box-sub">By article count</span>
          </div>
          {authorData.length === 0 ? <div className="an-empty">No data</div> :
            <div className="an-bar-wrap">
              {authorData.map(([author, count],i) => (
                <div key={author} className="an-bar-row">
                  <span className="an-bar-label" title={author}>{author}</span>
                  <div className="an-bar-track">
                    <div className="an-bar-fill" style={{width:`${(count/maxAuthor)*100}%`,
                      background:["#1d4ed8","#7c3aed","#0369a1","#065f46","#b45309"][i]||"#64748b"}}/>
                  </div>
                  <span className="an-bar-val">{count}</span>
                </div>
              ))}
            </div>
          }
        </div>
      </div>
    </div>
  );
}

function DashboardPanel({ articles, confs, subscribers, loading, setSection, openArt, openConf }) {
  return (
<div className="fu">
  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
    <div>
      <div className="pt">Dashboard</div>
      <div className="ps" style={{margin:0}}>Overview of Quantum Day content</div>
    </div>
    <div style={{background:"var(--s2)",border:"1px solid var(--bd)",borderRadius:10,padding:"12px 18px",textAlign:"right",boxShadow:"var(--sh)"}}>
      <div style={{fontSize:22,fontWeight:700,color:"var(--t)",fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",letterSpacing:"-.3px"}}>{new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</div>
      <div style={{fontSize:11,color:"var(--t4)",marginTop:2}}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
    </div>
  </div>
  <div className="sg">
    {[[articles.length,"Total Articles","article"],[articles.filter(a=>!a.status||a.status==="published").length,"Published","check"],[articles.filter(a=>a.status==="draft").length,"Drafts","edit"],[confs.length,"Conferences","calendar"],[subscribers.length,"Subscribers","mail"],[articles.filter(a=>a.featured).length,"Featured","star"]].map(([n,l,ic],i)=>(
      <div key={i} className="sc"><div className="si"><Ico name={ic} size={17}/></div><div className="sn">{n}</div><div className="sl">{l}</div></div>
    ))}
  </div>
  <div className="dg2">
    <div className="tc">
      <div className="th"><span className="tht">Top Articles by Views</span><button className="btn bp bsm" onClick={()=>{setSection("articles");openArt(null);}}><Ico name="plus" size={13}/>New</button></div>
      <table><tbody>
        {loading.a?<tr><td colSpan={4} style={{textAlign:"center",padding:32,color:"var(--t3)"}}>Loading…</td></tr>
        :[...articles].sort((a,b)=>(b.views||0)-(a.views||0)).slice(0,7).map((a,i)=>(<tr key={a.id} style={{cursor:"pointer"}} onClick={()=>{setSection("articles");openArt(a);}}>
          <td style={{width:24,color:"var(--t4)",fontSize:11,fontWeight:700,textAlign:"center"}}>{i+1}</td>
          <td><div className="tdt">{a.title}</div></td>
          <td><span className="chip ccat">{a.category}</span></td>
          <td style={{fontSize:12,fontWeight:700,color:"var(--t2)",whiteSpace:"nowrap",textAlign:"right",minWidth:48}}>{fmtViews(a.views)}<span style={{fontSize:10,color:"var(--t4)",fontWeight:400,marginLeft:3}}>views</span></td>
        </tr>))}
        {!loading.a&&articles.length===0&&<tr><td colSpan={4} style={{textAlign:"center",padding:32,color:"var(--t3)"}}>No articles yet</td></tr>}
      </tbody></table>
    </div>
    <div className="tc">
      <div className="th"><span className="tht">Upcoming Conferences</span><button className="btn bp bsm" onClick={()=>{setSection("conferences");openConf(null);}}><Ico name="plus" size={13}/>New</button></div>
      <table><tbody>
        {loading.c?<tr><td colSpan={3} style={{textAlign:"center",padding:32,color:"var(--t3)"}}>Loading…</td></tr>
        :confs.filter(c=>c.status==="upcoming").slice(0,5).map(c=>(<tr key={c.id} style={{cursor:"pointer"}} onClick={()=>{setSection("conferences");openConf(c);}}>
          <td><div className="tdt">{c.name}</div></td>
          <td><span className={`chip ${c.scale==="Global"?"cgl":"cre"}`}>{c.scale}</span></td>
          <td style={{fontSize:11,color:"var(--t3)",whiteSpace:"nowrap"}}>{fmt(c.date_start)}</td>
        </tr>))}
        {!loading.c&&confs.length===0&&<tr><td colSpan={3} style={{textAlign:"center",padding:32,color:"var(--t3)"}}>No conferences</td></tr>}
      </tbody></table>
    </div>
  </div>
</div>
  );
}


function ArticlesPanel({ articles, loading, openArt, delArt, token, fetchA, notify, setConfirm }) {
  const [selected,   setSelected]   = useState(new Set());
  const [filter,     setFilter]     = useState("all");   // all | published | draft | archived
  const [batchBusy,  setBatchBusy]  = useState(false);

  // ── Filtered list ──────────────────────────────────────────
  const filtered = filter === "all" ? articles
    : articles.filter(a => (a.status || "published") === filter);

  // ── Select helpers ─────────────────────────────────────────
  const allIds      = filtered.map(a => String(a.id));
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleOne = id => setSelected(prev => {
    const s = new Set(prev);
    s.has(String(id)) ? s.delete(String(id)) : s.add(String(id));
    return s;
  });

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const clearSelection = () => setSelected(new Set());

  // ── Batch actions ──────────────────────────────────────────
  const batchAction = async (action) => {
    const ids    = [...selected];
    const count  = ids.length;
    if (!count) return;

    const labels = {
      publish:  { title:"Publish Articles",  detail:`${count} article${count>1?"s":""} will be published and visible on the site.`,  confirmLabel:"Publish All",  statusVal:"published" },
      draft:    { title:"Move to Draft",     detail:`${count} article${count>1?"s":""} will be moved to draft and hidden from the site.`, confirmLabel:"Move to Draft", statusVal:"draft" },
      archive:  { title:"Archive Articles",  detail:`${count} article${count>1?"s":""} will be archived and hidden from the site.`, confirmLabel:"Archive All",  statusVal:"archived" },
      delete:   { title:"Delete Articles",   detail:`Permanently delete ${count} article${count>1?"s":""}? This cannot be undone.`,   confirmLabel:"Delete All",   statusVal:null },
    };
    const cfg = labels[action];

    setConfirm({
      type:    action === "delete" ? "danger" : "warning",
      icon:    action === "delete" ? "trash" : "edit",
      title:   cfg.title,
      msg:     `Apply to ${count} selected article${count>1?"s":""}?`,
      detail:  cfg.detail,
      confirmLabel: cfg.confirmLabel,
      onOk: async () => {
        setConfirm(null);
        setBatchBusy(true);
        let done = 0, failed = 0;
        try {
          for (const id of ids) {
            try {
              if (action === "delete") {
                await api.deleteArticle(id, token);
              } else {
                const art = articles.find(a => String(a.id) === String(id));
                if (art) await api.saveArticle({ ...art, status: cfg.statusVal }, token);
              }
              done++;
            } catch { failed++; }
          }
          await fetchA();
          clearSelection();
          if (failed === 0) notify(`✅ ${done} article${done>1?"s":""} ${action === "delete" ? "deleted" : action === "publish" ? "published" : action === "draft" ? "moved to draft" : "archived"}`);
          else notify(`⚠ ${done} succeeded, ${failed} failed`, "error");
        } finally { setBatchBusy(false); }
      }
    });
  };

  const pubCount     = articles.filter(a=>!a.status||a.status==="published").length;
  const draftCount   = articles.filter(a=>a.status==="draft").length;
  const archCount    = articles.filter(a=>a.status==="archived").length;

  return (
<div className="fu">
  <div className="pt">Articles</div>
  <div className="ps">Manage all news articles and research content
    <span style={{marginLeft:12,fontSize:12}}>
      <span style={{color:"#2e7d32",fontWeight:600}}>{pubCount} published</span>
      {draftCount>0&&<span style={{color:"#856404",fontWeight:600,marginLeft:8}}>{draftCount} draft{draftCount>1?"s":""}</span>}
      {archCount>0&&<span style={{color:"var(--t4)",fontWeight:500,marginLeft:8}}>{archCount} archived</span>}
    </span>
  </div>

  {/* ── Filter tabs ── */}
  <div style={{display:"flex",gap:4,marginBottom:14,flexWrap:"wrap"}}>
    {[["all","All",articles.length],["published","Published",pubCount],["draft","Draft",draftCount],["archived","Archived",archCount]].map(([val,lbl,cnt])=>(
      <button key={val} onClick={()=>{setFilter(val);clearSelection();}}
        style={{padding:"5px 14px",borderRadius:7,border:"1px solid",fontSize:12,fontWeight:600,cursor:"pointer",
          borderColor:filter===val?"var(--ac)":"var(--bd)",
          background:filter===val?"var(--ac)":"var(--bg)",
          color:filter===val?"#fff":"var(--t2)"}}>
        {lbl} <span style={{opacity:.65}}>({cnt})</span>
      </button>
    ))}
  </div>

  <div className="tc">
    {/* ── Table header with batch action bar ── */}
    <div className="th" style={{flexWrap:"wrap",gap:8}}>
      <span className="tht">{filtered.length} article{filtered.length!==1?"s":""}</span>

      {/* Batch action bar — visible only when items selected */}
      {someSelected ? (
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",flex:1}}>
          <span style={{fontSize:12,fontWeight:600,color:"var(--ac)",background:"var(--acl)",
            padding:"3px 10px",borderRadius:20,whiteSpace:"nowrap"}}>
            {selected.size} selected
          </span>
          <button disabled={batchBusy} onClick={()=>batchAction("publish")}
            style={{padding:"5px 12px",background:"#2e7d32",color:"#fff",border:"none",borderRadius:6,
              fontSize:12,fontWeight:600,cursor:"pointer",opacity:batchBusy?.6:1,whiteSpace:"nowrap"}}>
            ✅ Publish
          </button>
          <button disabled={batchBusy} onClick={()=>batchAction("draft")}
            style={{padding:"5px 12px",background:"#856404",color:"#fff",border:"none",borderRadius:6,
              fontSize:12,fontWeight:600,cursor:"pointer",opacity:batchBusy?.6:1,whiteSpace:"nowrap"}}>
            ✏️ Draft
          </button>
          <button disabled={batchBusy} onClick={()=>batchAction("archive")}
            style={{padding:"5px 12px",background:"var(--t3)",color:"#fff",border:"none",borderRadius:6,
              fontSize:12,fontWeight:600,cursor:"pointer",opacity:batchBusy?.6:1,whiteSpace:"nowrap"}}>
            📦 Archive
          </button>
          <button disabled={batchBusy} onClick={()=>batchAction("delete")}
            style={{padding:"5px 12px",background:"var(--err)",color:"#fff",border:"none",borderRadius:6,
              fontSize:12,fontWeight:600,cursor:"pointer",opacity:batchBusy?.6:1,whiteSpace:"nowrap"}}>
            🗑 Delete
          </button>
          <button onClick={clearSelection}
            style={{padding:"5px 10px",background:"none",border:"1px solid var(--bd)",borderRadius:6,
              fontSize:12,color:"var(--t3)",cursor:"pointer",whiteSpace:"nowrap"}}>
            ✕ Clear
          </button>
          {batchBusy && <span style={{fontSize:11,color:"var(--t4)"}}>Processing…</span>}
        </div>
      ) : (
        <button className="btn bp" onClick={()=>openArt(null)}><Ico name="plus" size={14}/>New Article</button>
      )}
    </div>

    <div style={{overflowX:"auto"}}><table>
      <thead><tr>
        <th style={{width:36}}>
          <input type="checkbox"
            checked={allSelected}
            ref={el => { if(el) el.indeterminate = !allSelected && someSelected && filtered.some(a=>selected.has(String(a.id))); }}
            onChange={toggleAll}
            style={{width:15,height:15,accentColor:"var(--ac)",cursor:"pointer"}}
            title={allSelected?"Deselect all":"Select all"}/>
        </th>
        <th>Title</th><th>Category</th><th>Author</th><th>Date</th><th>Views</th><th>Status</th><th>Featured</th><th>Actions</th>
      </tr></thead>
      <tbody>
        {loading.a
          ? <tr><td colSpan={9} style={{textAlign:"center",padding:40,color:"var(--t3)"}}>Loading…</td></tr>
          : filtered.length===0
            ? <tr><td colSpan={9} style={{textAlign:"center",padding:48,color:"var(--t3)"}}>No articles found.</td></tr>
            : filtered.map(a=>{
                const isChecked = selected.has(String(a.id));
                return (
                <tr key={a.id} style={{background:isChecked?"var(--acl2)":"",transition:"background .1s"}}>
                  <td style={{textAlign:"center"}}>
                    <input type="checkbox" checked={isChecked} onChange={()=>toggleOne(a.id)}
                      style={{width:15,height:15,accentColor:"var(--ac)",cursor:"pointer"}}/>
                  </td>
                  <td style={{maxWidth:240}}><div className="tdt" onClick={()=>openArt(a)}>{a.title}</div></td>
                  <td><span className="chip ccat">{a.category}</span></td>
                  <td style={{fontSize:13}}>{a.author}</td>
                  <td style={{fontSize:12,color:"var(--t3)",whiteSpace:"nowrap"}}>{fmt(a.published_at)}</td>
                  <td style={{fontSize:13,fontWeight:600,color:"var(--t2)",textAlign:"center"}}>{fmtViews(a.views)}</td>
                  <td>{a.status==="draft"?<span className="chip cdraft">✏️ Draft</span>:a.status==="archived"?<span className="chip carch">📦 Archived</span>:<span className="chip cpub">✅ Published</span>}</td>
                  <td>{a.featured?<span className="chip cft"><Ico name="star" size={10}/>Yes</span>:<span style={{color:"var(--t3)"}}>—</span>}</td>
                  <td><div className="ar">
                    <button className="btn bse bsm bic" onClick={()=>openArt(a)}><Ico name="edit" size={13}/></button>
                    <button className="btn bd bsm bic" onClick={()=>delArt(a.id,a.title)}><Ico name="trash" size={13}/></button>
                  </div></td>
                </tr>
              );})}
      </tbody>
    </table></div>
  </div>
</div>
  );
}


function ConferencesPanel({ confs, loading, openConf, delConf }) {
  return (
<div className="fu">
  <div className="pt">Conferences</div><div className="ps">Manage global and regional quantum events</div>
  <div className="tc">
    <div className="th"><span className="tht">{confs.length} conference{confs.length!==1?"s":""}</span><button className="btn bp" onClick={()=>openConf(null)}><Ico name="plus" size={14}/>New Conference</button></div>
    <div style={{overflowX:"auto"}}><table>
      <thead><tr><th>Name</th><th>Scale</th><th>Location</th><th>Date</th><th>Status</th><th>Featured</th><th>Actions</th></tr></thead>
      <tbody>
        {loading.c?<tr><td colSpan={7} style={{textAlign:"center",padding:40,color:"var(--t3)"}}>Loading…</td></tr>
        :confs.length===0?<tr><td colSpan={7} style={{textAlign:"center",padding:48,color:"var(--t3)"}}>No conferences yet.</td></tr>
        :confs.map(c=>(<tr key={c.id}>
          <td style={{maxWidth:200}}><div className="tdt" onClick={()=>openConf(c)}>{c.name}</div></td>
          <td><span className={`chip ${c.scale==="Global"?"cgl":"cre"}`}>{c.scale}</span></td>
          <td style={{fontSize:12}}>{c.location}</td>
          <td style={{fontSize:12,color:"var(--t3)",whiteSpace:"nowrap"}}>{fmt(c.date_start)}</td>
          <td><span className={`chip ${c.status==="upcoming"?"cok":"ctag"}`}>{c.status}</span></td>
          <td>{c.featured?<Ico name="check" size={15} style={{color:"var(--ok)"}}/>:<span style={{color:"var(--t3)"}}>—</span>}</td>
          <td><div className="ar"><button className="btn bse bsm bic" onClick={()=>openConf(c)}><Ico name="edit" size={13}/></button><button className="btn bd bsm bic" onClick={()=>delConf(c.id,c.name)}><Ico name="trash" size={13}/></button></div></td>
        </tr>))}
      </tbody>
    </table></div>
  </div>
</div>
  );
}


function MenusPanel({ menus, setMenus, token, notify, fetchMenus, setConfirm }) {
  const [localMenus,setLocalMenus] = useState(menus || {navbar:[],footer_explore:[],footer_topics:[],footer_company:[]});
  const [activeTab,setActiveTab]   = useState("navbar");
  const [editingId,setEditingId]   = useState(null);
  const [editForm,setEditForm]     = useState({});
  const [savingMenus,setSavingMenus] = useState(false);
  const [dragOver,setDragOver]     = useState(null);
  const dragItem = useRef(null);

  const tabs=[
    {id:"navbar",        label:"Navbar",          desc:"Main navigation menu at the top of the page"},
    {id:"footer_explore",label:"Footer — Explore", desc:"Explore column in the footer"},
    {id:"footer_topics", label:"Footer — Topics",  desc:"Topics column in the footer (article filter)"},
    {id:"footer_company",label:"Footer — Company", desc:"Company column in the footer"},
  ];

  const items = localMenus[activeTab] || [];
  const updateItems = newItems => setLocalMenus(p=>({...p,[activeTab]:newItems}));

  // Toggle enable/disable
  const toggleItem = id => updateItems(items.map(m=>m.id===id?{...m,enabled:!m.enabled}:m));

  // Add new item
  const addItem = () => {
    const newId = `${activeTab}_${Date.now()}`;
    const newItem = activeTab==="footer_topics"
      ? {id:newId, label:"New Topic",  filter:"", enabled:true}
      : activeTab==="footer_company"
      ? {id:newId, label:"New Link",   url:"", page:"", enabled:true}
      : {id:newId, label:"New Item",   page:"home", url:"", enabled:true};
    updateItems([...items, newItem]);
    startEdit(newItem);
  };

  // Pindah urutan (tombol panah)
  const moveItem = (id, dir) => {
    const idx = items.findIndex(m=>m.id===id);
    if(dir===-1&&idx===0) return;
    if(dir===1&&idx===items.length-1) return;
    const arr=[...items];
    [arr[idx],arr[idx+dir]]=[arr[idx+dir],arr[idx]];
    updateItems(arr);
  };

  // Drag & drop reorder
  const onDragStart = (e, idx) => { dragItem.current = idx; e.dataTransfer.effectAllowed="move"; };
  const onDragOver  = (e, idx) => { e.preventDefault(); setDragOver(idx); };
  const onDrop      = (e, idx) => {
    e.preventDefault();
    if(dragItem.current === null || dragItem.current === idx) { setDragOver(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(dragItem.current, 1);
    arr.splice(idx, 0, moved);
    updateItems(arr);
    dragItem.current = null; setDragOver(null);
  };

  // Edit item
  const startEdit = item => { setEditingId(item.id); setEditForm({...item}); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = () => {
    if(!editForm.label?.trim()) return;
    updateItems(items.map(m=>m.id===editingId?{...editForm,label:editForm.label.trim()}:m));
    setEditingId(null); setEditForm({});
  };

  // Duplicate item
  const duplicateItem = id => {
    const src = items.find(m=>m.id===id);
    if(!src) return;
    const newItem = {...src, id:`${activeTab}_${Date.now()}`, label:src.label+" (copy)"};
    const idx = items.findIndex(m=>m.id===id);
    const arr = [...items];
    arr.splice(idx+1, 0, newItem);
    updateItems(arr);
  };

  // Save everything to Supabase
  const handleSave = (confirmed=false) => {
    if(!confirmed) return setConfirm({
      type:"warning",icon:"globe",title:"Publish Menu Changes",
      msg:"Save and publish these menu changes to the live website?",
      detail:"Navigation will update immediately for all visitors.",
      confirmLabel:"Save & Publish",
      onOk:()=>{ setConfirm(null); handleSave(true); }
    });
    setSavingMenus(true);
    api.saveMenus(localMenus,token)
      .then(()=>{ setMenus(localMenus); notify("Menu saved — changes are live on the website"); })
      .catch(e=>notify("Save failed: "+e.message,"error"))
      .finally(()=>setSavingMenus(false));
  };

  const pageOptions=["home","news","conferences","contact"];
  const isNavOrExplore = activeTab==="navbar"||activeTab==="footer_explore";
  const isTopics       = activeTab==="footer_topics";
  const isCompany      = activeTab==="footer_company";

  return(<div className="fu">
    <div className="pt">Menu Manager</div>
    <div className="ps">Manage navigation and footer menus — changes go live immediately</div>

    {/* Tab selector */}
    <div style={{display:"flex",gap:8,marginBottom:24,flexWrap:"wrap"}}>
      {tabs.map(t=>(
        <button key={t.id} onClick={()=>{setActiveTab(t.id);setEditingId(null);}}
          className={`btn ${activeTab===t.id?"bp":"bse"}`} style={{fontSize:12,padding:"6px 14px"}}>
          {t.label}
          <span style={{marginLeft:6,background:activeTab===t.id?"rgba(255,255,255,.25)":"var(--bd)",borderRadius:10,padding:"1px 7px",fontSize:11}}>
            {(localMenus[t.id]||[]).length}
          </span>
        </button>
      ))}
    </div>

    <div className="tc">
      <div className="th">
        <div>
          <span className="tht">{tabs.find(t=>t.id===activeTab)?.label}</span>
          <span style={{fontSize:12,color:"var(--t3)",marginLeft:10}}>{tabs.find(t=>t.id===activeTab)?.desc}</span>
        </div>
        <button className="btn bp bsm" onClick={addItem}><Ico name="plus" size={13}/>Add Item</button>
      </div>

      <div style={{padding:"4px 0"}}>
        {items.length===0&&<div style={{textAlign:"center",padding:40,color:"var(--t3)"}}>No menu items yet. Click <strong>Add Item</strong> to start.</div>}
        {items.map((m,idx)=>(
          <div key={m.id}
            draggable onDragStart={e=>onDragStart(e,idx)} onDragOver={e=>onDragOver(e,idx)} onDrop={e=>onDrop(e,idx)} onDragLeave={()=>setDragOver(null)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",
              borderBottom:"1px solid var(--bd2)",
              borderTop:dragOver===idx?"2px solid var(--ac)":"2px solid transparent",
              background:editingId===m.id?"var(--am)":m.enabled?"":"rgba(0,0,0,.02)",
              opacity:m.enabled?1:.6, cursor:"grab", transition:"background .1s"}}>

            {/* Drag handle + arrow buttons */}
            <div style={{display:"flex",flexDirection:"column",gap:2,opacity:.4,cursor:"grab"}} title="Drag to reorder">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="18" x2="16" y2="18"/></svg>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <button className="btn bse bic" style={{padding:3,borderRadius:4}} onClick={()=>moveItem(m.id,-1)} disabled={idx===0} title="Move up">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15"/></svg>
              </button>
              <button className="btn bse bic" style={{padding:3,borderRadius:4}} onClick={()=>moveItem(m.id,1)} disabled={idx===items.length-1} title="Move down">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>

            {editingId===m.id ? (
              /* ── EDIT MODE ── */
              <div style={{flex:1,display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,alignItems:"end"}}>
                <div className="fgr">
                  <label className="fl">Label (display text)</label>
                  <input className="fc2" value={editForm.label||""} autoFocus
                    onChange={e=>setEditForm(p=>({...p,label:e.target.value}))}
                    onKeyDown={e=>{if(e.key==="Enter")saveEdit();if(e.key==="Escape")cancelEdit();}}/>
                </div>
                {isNavOrExplore&&(
                  <>
                    <div className="fgr">
                      <label className="fl">Landing Page</label>
                      <select className="fc2" value={editForm.page||"home"} onChange={e=>setEditForm(p=>({...p,page:e.target.value,url:""}))}>
                        {pageOptions.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                        <option value="">— External URL —</option>
                      </select>
                    </div>
                    {!editForm.page&&(
                      <div className="fgr" style={{gridColumn:"1/-1"}}>
                        <label className="fl">External URL</label>
                        <input className="fc2" value={editForm.url||""} onChange={e=>setEditForm(p=>({...p,url:e.target.value}))} placeholder="https://…"/>
                      </div>
                    )}
                  </>
                )}
                {isTopics&&(
                  <div className="fgr">
                    <label className="fl">Filter Category</label>
                    <input className="fc2" value={editForm.filter||""} onChange={e=>setEditForm(p=>({...p,filter:e.target.value}))} placeholder="Research, Technology, …"/>
                  </div>
                )}
                {isCompany&&(
                  <>
                    <div className="fgr">
                      <label className="fl">Landing Page (opsional)</label>
                      <select className="fc2" value={editForm.page||""} onChange={e=>setEditForm(p=>({...p,page:e.target.value,url:""}))}>
                        <option value="">— Select page —</option>
                        {pageOptions.map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                        <option value="">— External URL —</option>
                      </select>
                    </div>
                    {!editForm.page&&(
                      <div className="fgr">
                        <label className="fl">External URL</label>
                        <input className="fc2" value={editForm.url||""} onChange={e=>setEditForm(p=>({...p,url:e.target.value}))} placeholder="https://…"/>
                      </div>
                    )}
                  </>
                )}
                <div style={{display:"flex",gap:8,gridColumn:"1/-1",justifyContent:"flex-end",marginTop:4}}>
                  <button className="btn bse bsm" onClick={cancelEdit}>Cancel</button>
                  <button className="btn bp bsm" onClick={saveEdit} disabled={!editForm.label?.trim()}><Ico name="save" size={12}/>Save</button>
                </div>
              </div>
            ) : (
              /* ── VIEW MODE ── */
              <div style={{flex:1,display:"flex",alignItems:"center",gap:12,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14,color:"var(--t)",minWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.label}</div>
                <div style={{fontSize:12,color:"var(--t3)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {isNavOrExplore&&(m.url
                    ? <span>→ <a href={m.url} target="_blank" rel="noopener noreferrer" style={{color:"var(--ac)"}}>{m.url}</a></span>
                    : <span>→ page <code style={{background:"var(--bg2)",padding:"1px 6px",borderRadius:4}}>{m.page||"home"}</code></span>)}
                  {isTopics&&<span>filter: <code style={{background:"var(--bg2)",padding:"1px 6px",borderRadius:4}}>{m.filter||"—"}</code></span>}
                  {isCompany&&(m.url
                    ? <a href={m.url} target="_blank" rel="noopener noreferrer" style={{color:"var(--ac)"}}>{m.url}</a>
                    : m.page ? <span>→ page <code style={{background:"var(--bg2)",padding:"1px 6px",borderRadius:4}}>{m.page}</code></span>
                    : <span style={{color:"var(--t3)"}}>No link</span>)}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                  <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",fontSize:12,color:"var(--t3)",userSelect:"none"}}>
                    <input type="checkbox" checked={!!m.enabled} onChange={()=>toggleItem(m.id)} style={{accentColor:"var(--ac)"}}/>
                    {m.enabled?"Active":"Off"}
                  </label>
                  <button className="btn bse bsm bic" onClick={()=>startEdit(m)} title="Edit"><Ico name="edit" size={13}/></button>
                  <button className="btn bse bsm bic" onClick={()=>duplicateItem(m.id)} title="Duplicate" style={{opacity:.7}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                  <button className="btn bd bsm bic" onClick={()=>setConfirm({type:"danger",icon:"trash",title:"Remove Menu Item",msg:`Remove "${m.label}" from this menu?`,confirmLabel:"Remove",onOk:()=>{updateItems(items.filter(x=>x.id!==m.id));setConfirm(null);}})} title="Delete"><Ico name="trash" size={13}/></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Preview Navbar */}
    <div style={{marginTop:20,background:"var(--s)",border:"1px solid var(--bd)",borderRadius:14,padding:"18px 22px"}}>
      <div style={{fontSize:11,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:"var(--t3)",marginBottom:12}}>Preview Navbar</div>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"var(--ac)",borderRadius:8,padding:"6px 12px",color:"#e8f0f8",fontSize:13,fontWeight:700,marginRight:8}}>
          ⚛ Quantum Day
        </div>
        {(localMenus.navbar||[]).filter(m=>m.enabled).map(m=>(
          <div key={m.id} style={{padding:"6px 14px",borderRadius:7,fontSize:13,background:"var(--am)",color:"var(--ac)",fontWeight:500}}>{m.label}</div>
        ))}
        <div style={{padding:"6px 14px",borderRadius:7,fontSize:13,background:"var(--ac)",color:"#e8f0f8",fontWeight:700,marginLeft:"auto"}}>Subscribe</div>
      </div>
    </div>

    <div style={{display:"flex",justifyContent:"flex-end",marginTop:20,gap:10}}>
      <button className="btn bse" onClick={()=>setConfirm({type:"warning",icon:"alert",title:"Reset Menus",msg:"Discard all unsaved changes and reload from database?",confirmLabel:"Reset",onOk:()=>{fetchMenus();setConfirm(null);}})}>Reset</button>
      <button className="btn bp" onClick={handleSave} disabled={savingMenus}>
        {savingMenus?<><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Saving…</>:<><Ico name="save" size={14}/>Save & Publish to Website</>}
      </button>
    </div>
  </div>);
}


function SubscribersPanel({ subscribers, loadingSubs, fetchSubs, token, notify, setConfirm }) {
  const delSub=id=>setConfirm({type:"danger",icon:"trash",title:"Delete Subscriber",msg:"Delete this subscriber?",detail:"Their email will be permanently removed from the newsletter list.",confirmLabel:"Delete Subscriber",onOk:async()=>{
    try{ await api.deleteSubscriber(id,token); await fetchSubs(); notify("Subscriber deleted"); }
    catch(e){ notify("Delete failed: "+e.message,"error"); }
    setConfirm(null);
  }});
  const exportCSV=()=>{
    const rows=["Email,Date Registered",...subscribers.map(s=>`${s.email},${fmt(s.created_at)}`)];
    const blob=new Blob([rows.join("\n")],{type:"text/csv"});
    const a=document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download="subscribers.csv"; a.click();
  };
  return(<div className="fu">
    <div className="pt">Subscribers</div><div className="ps">List of emails subscribed to Quantum Day newsletter</div>
    <div className="tc">
      <div className="th">
        <span className="tht">{subscribers.length} subscriber{subscribers.length!==1?"s":""}</span>
        <button className="btn bse" onClick={exportCSV} disabled={subscribers.length===0}><Ico name="save" size={13}/>Export CSV</button>
      </div>
      <div style={{overflowX:"auto"}}><table>
        <thead><tr><th>Email</th><th>Status</th><th>Date Registered</th><th>Actions</th></tr></thead>
        <tbody>
          {loadingSubs?<tr><td colSpan={4} style={{textAlign:"center",padding:40,color:"var(--t3)"}}>Loading…</td></tr>
          :subscribers.length===0?<tr><td colSpan={4} style={{textAlign:"center",padding:48,color:"var(--t3)"}}>No subscribers yet.</td></tr>
          :subscribers.map(s=>(<tr key={s.id}>
            <td style={{fontFamily:"monospace",fontSize:13}}>{s.email}</td>
            <td>{s.verified
              ? <span className="chip cok"><Ico name="check" size={10}/>Verified</span>
              : <span className="chip" style={{background:"var(--warnb)",color:"var(--warn)"}}>⏳ Pending</span>}</td>
            <td style={{fontSize:12,color:"var(--t3)",whiteSpace:"nowrap"}}>{fmt(s.created_at)}</td>
            <td><button className="btn bd bsm bic" onClick={()=>delSub(s.id)}><Ico name="trash" size={13}/></button></td>
          </tr>))}
        </tbody>
      </table></div>
    </div>
  </div>);
}


function SettingsPanel({ siteForm, setSiteForm, saveAllSettings, savingSettings }) {
  return (
<div className="fu">
  <div className="pt">Settings</div><div className="ps">Configure site information and SEO</div>
  <div className="setc">
    <div className="sets">
      <div className="sett">Database Connection</div>
      <div className="setsub">Status of your Supabase integration</div>
      <div className="dbbadge"><Ico name="check" size={12}/>Supabase Connected via Secure API</div>
      <div className="dbinfo">
        <strong>Architecture:</strong> Serverless API — credentials stored server-side only<br/>
        <strong>Data sync:</strong> Changes in admin are instantly visible on the main site<br/>
        <strong>Tables:</strong> <code>articles</code> · <code>conferences</code> · <code>site_settings</code>
      </div>
    </div>
    <div className="sets">
      <div className="sett">Site Identity & SEO</div>
      <div className="setsub">Changes are saved to Supabase and reflected on the main site</div>
      <div className="fg">
        <div className="fgr"><label className="fl">Site Name</label><input className="fc2" value={siteForm.site_name||""} onChange={e=>setSiteForm(p=>({...p,site_name:e.target.value}))}/></div>
        <div className="fgr"><label className="fl">Tagline</label><input className="fc2" value={siteForm.tagline||""} onChange={e=>setSiteForm(p=>({...p,tagline:e.target.value}))}/></div>
        <div className="fgr full"><label className="fl">Meta Description (SEO)</label><textarea className="fc2" value={siteForm.description||""} onChange={e=>setSiteForm(p=>({...p,description:e.target.value}))}/><span className="fhint">Aim for 150–160 characters.</span></div>
        <div className="fgr full"><label className="fl">SEO Keywords</label><input className="fc2" value={siteForm.keywords||""} onChange={e=>setSiteForm(p=>({...p,keywords:e.target.value}))} placeholder="quantum computing, quantum news"/><span className="fhint">Comma-separated</span></div>
      </div>
    </div>
    <div className="sets">
      <div className="sett">Contact Form</div>
      <div className="setsub">Email address for contact form notifications — submissions also saved in the Submissions section</div>
      <div className="fg">
        <div className="fgr full"><label className="fl">Recipient Email</label><input className="fc2" type="email" value={siteForm.contact_email||""} onChange={e=>setSiteForm(p=>({...p,contact_email:e.target.value}))} placeholder="contact@quantum-day.com"/></div>
      </div>
    </div>
    <div style={{display:"flex",justifyContent:"flex-end"}}>
      <button className="btn bp" onClick={saveAllSettings} disabled={savingSettings}>{savingSettings?<><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Saving…</>:<><Ico name="save" size={14}/>Save Settings</>}</button>
    </div>
  </div>
</div>
  );
}


function ArtModal({ form, setForm, setModal, saveArt, saving, token }) {
  return (
<div className="ov" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
  <div className="mo fu" onClick={e=>e.stopPropagation()}>
    <div className="moh"><span className="mot">{form.id?"Edit Article":"New Article"}</span><button className="moc" onClick={()=>setModal(null)}><Ico name="x" size={18}/></button></div>
    <div className="fg">
      <div className="fgr full"><label className="fl">Title *</label><input className="fc2" value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">Category</label><select className="fc2" value={form.category||"Research"} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{["Research","Technology","Conference","Security","Business","Policy","Cryptocurrency"].map(c=><option key={c}>{c}</option>)}</select></div>
      <div className="fgr"><label className="fl">Tag</label><input className="fc2" value={form.tag||""} onChange={e=>setForm(p=>({...p,tag:e.target.value}))} placeholder="e.g. Breakthrough"/></div>
      <div className="fgr"><label className="fl">Author *</label><input className="fc2" value={form.author||""} onChange={e=>setForm(p=>({...p,author:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">Author Role</label><input className="fc2" value={form.author_role||""} onChange={e=>setForm(p=>({...p,author_role:e.target.value}))} placeholder="Senior Correspondent"/></div>
      <div className="fgr"><label className="fl">Publication Date</label><input className="fc2" type="date" value={form.published_at||""} onChange={e=>setForm(p=>({...p,published_at:e.target.value}))}/></div>
      <div className="fgr">
        <label className="fl">Read Time
          <span style={{marginLeft:6,fontSize:10,fontWeight:500,color:"var(--t4)",background:"var(--bg2)",padding:"1px 6px",borderRadius:10}}>AUTO</span>
        </label>
        <div style={{position:"relative"}}>
          <input className="fc2" value={form.read_time||""} onChange={e=>setForm(p=>({...p,read_time:e.target.value}))}
            placeholder={calcReadTime(form.content,form.excerpt)||"auto"}
            style={{paddingRight:72}}/>
          {(form.content||form.excerpt) && (
            <button type="button" onClick={()=>setForm(p=>({...p,read_time:calcReadTime(p.content,p.excerpt)}))}
              style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",
                fontSize:10,fontWeight:600,padding:"3px 8px",borderRadius:5,border:"1px solid var(--bd)",
                background:"var(--bg2)",color:"var(--navy)",cursor:"pointer",whiteSpace:"nowrap"}}>
              ↻ Auto
            </button>
          )}
        </div>
        <span className="fhint">
          Auto: {calcReadTime(form.content,form.excerpt)||"—"} &nbsp;·&nbsp; {((form.content||"")+" "+(form.excerpt||"")).trim().split(/\s+/).filter(Boolean).length} words &nbsp;·&nbsp; Edit to override
        </span>
      </div>
      <div className="fgr"><label className="fl">Fallback Style (if no image available)</label>
        <select className="fc2" value={form.image_style||form.image||"atom"} onChange={e=>setForm(p=>({...p,image_style:e.target.value}))}>
          {["atom","globe","cpu","shield","chart"].map(v=><option key={v} value={v}>{v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
        </select>
        <span className="fhint">SVG illustration shown when no image available</span>
      </div>
      {/* IMAGE UPLOAD — hero image artikel */}
      <div className="fgr full">
        <ImageUploader token={token} currentUrl={form.image_url||""} label="Hero Image (displayed at the top of the article)" hint="Recommended: 1200×630px · JPG/PNG/WebP · max 5MB" onUploaded={url=>setForm(p=>({...p,image_url:url}))}/>
      </div>
      {/* IMAGE UPLOAD — thumbnail (list view) */}
      <div className="fgr full">
        <ImageUploader token={token} currentUrl={form.thumbnail_url||""} label="Thumbnail (shown in article list)" hint="Recommended: 400×267px · JPG/PNG/WebP · max 2MB" onUploaded={url=>setForm(p=>({...p,thumbnail_url:url}))}/>
      </div>
      <div className="fgr full"><label className="fl">Excerpt</label><textarea className="fc2" value={form.excerpt||""} onChange={e=>setForm(p=>({...p,excerpt:e.target.value}))} placeholder="Brief summary shown on article cards…"/></div>
      <div className="fgr full"><label className="fl">Full Content</label>
        <textarea className="fc2 tall" value={form.content||""} onChange={e=>{
          const newContent=e.target.value;
          setForm(p=>{
            const autoTime=calcReadTime(p.content,p.excerpt);
            const shouldAuto=!p.read_time||p.read_time===autoTime||p.read_time===calcReadTime(newContent,p.excerpt);
            return {...p, content:newContent, read_time:shouldAuto?calcReadTime(newContent,p.excerpt):p.read_time};
          });
        }} placeholder="Full article. Separate paragraphs with a blank line."/>
        <span className="fhint">Separate paragraphs with blank line (double Enter)</span>
      </div>
      <div className="fgr full">
        <label className="fl">Publication Status</label>
        <div className="status-toggle">
          <button className={`on sdraft${form.status==="draft"?" on sdraft":""}`}
            style={{color:form.status==="draft"?"#fff":"var(--t3)",background:form.status==="draft"?"#856404":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"draft"}))}>
            ✏️ Draft
          </button>
          <button
            style={{color:form.status==="published"?"#fff":"var(--t3)",background:form.status==="published"?"#2e7d32":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"published"}))}>
            ✅ Published
          </button>
          <button
            style={{color:form.status==="archived"?"#fff":"var(--t3)",background:form.status==="archived"?"#636366":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"archived"}))}>
            📦 Archived
          </button>
        </div>
        <span className="fhint">{form.status==="draft"?"Not visible to public — save without publishing":form.status==="archived"?"Hidden from public — preserved for reference":"Live on the website and visible to all visitors"}</span>
      </div>
      <div className="fgr full"><label className="fchk"><input type="checkbox" checked={!!form.featured} onChange={e=>setForm(p=>({...p,featured:e.target.checked}))}/><span>Feature on homepage</span></label></div>
    </div>
    <div className="mof">
      <button className="btn bse" onClick={()=>setModal(null)}>Cancel</button>
      <button className="btn bp" onClick={saveArt} disabled={saving} style={form.status==="draft"?{background:"#856404"}:form.status==="archived"?{background:"var(--t4)"}:{}}>{saving?<><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Saving…</>:<><Ico name="save" size={14}/>{form.id?(form.status==="draft"?"Save Draft":form.status==="archived"?"Archive":"Save & Publish"):(form.status==="draft"?"Create Draft":form.status==="archived"?"Archive":"Create & Publish")}</>}</button>
    </div>
  </div>
</div>
  );
}


function ConfModal({ form, setForm, setModal, saveConf, saving }) {
  return (
<div className="ov" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
  <div className="mo fu" onClick={e=>e.stopPropagation()}>
    <div className="moh"><span className="mot">{form.id?"Edit Conference":"New Conference"}</span><button className="moc" onClick={()=>setModal(null)}><Ico name="x" size={18}/></button></div>
    <div className="fg">
      <div className="fgr full"><label className="fl">Conference Name *</label><input className="fc2" value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">Scale</label><select className="fc2" value={form.scale||"Global"} onChange={e=>setForm(p=>({...p,scale:e.target.value}))}><option>Global</option><option>Regional</option></select></div>
      <div className="fgr"><label className="fl">Status</label><select className="fc2" value={form.status||"upcoming"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}><option value="upcoming">Upcoming</option><option value="ongoing">Ongoing</option><option value="past">Past</option><option value="cancelled">Cancelled</option></select></div>
      <div className="fgr"><label className="fl">Start Date *</label><input className="fc2" type="date" value={form.date_start||""} onChange={e=>setForm(p=>({...p,date_start:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">End Date</label><input className="fc2" type="date" value={form.date_end||""} onChange={e=>setForm(p=>({...p,date_end:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">Location</label><input className="fc2" value={form.location||""} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="London, United Kingdom"/></div>
      <div className="fgr"><label className="fl">Venue</label><input className="fc2" value={form.venue||""} onChange={e=>setForm(p=>({...p,venue:e.target.value}))}/></div>
      <div className="fgr"><label className="fl">Expected Attendees</label><input className="fc2" value={form.attendees||""} onChange={e=>setForm(p=>({...p,attendees:e.target.value}))} placeholder="3,000+"/></div>
      <div className="fgr"><label className="fl">Website URL</label><input className="fc2" value={form.website||""} onChange={e=>setForm(p=>({...p,website:e.target.value}))} placeholder="https://…"/></div>
      <div className="fgr full"><label className="fl">Topics</label><input className="fc2" value={form.topics||""} onChange={e=>setForm(p=>({...p,topics:e.target.value}))} placeholder="Quantum AI, Error Correction"/><span className="fhint">Comma-separated</span></div>
      <div className="fgr full">
        <label className="fl">Publication Status</label>
        <div className="status-toggle">
          <button className={`on sdraft${form.status==="draft"?" on sdraft":""}`}
            style={{color:form.status==="draft"?"#fff":"var(--t3)",background:form.status==="draft"?"#856404":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"draft"}))}>
            ✏️ Draft
          </button>
          <button
            style={{color:form.status==="published"?"#fff":"var(--t3)",background:form.status==="published"?"#2e7d32":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"published"}))}>
            ✅ Published
          </button>
          <button
            style={{color:form.status==="archived"?"#fff":"var(--t3)",background:form.status==="archived"?"#636366":"var(--bg)"}}
            onClick={()=>setForm(p=>({...p,status:"archived"}))}>
            📦 Archived
          </button>
        </div>
        <span className="fhint">{form.status==="draft"?"Not visible to public — save without publishing":form.status==="archived"?"Hidden from public — preserved for reference":"Live on the website and visible to all visitors"}</span>
      </div>
      <div className="fgr full"><label className="fchk"><input type="checkbox" checked={!!form.featured} onChange={e=>setForm(p=>({...p,featured:e.target.checked}))}/><span>Feature on homepage</span></label></div>
    </div>
    <div className="mof">
      <button className="btn bse" onClick={()=>setModal(null)}>Cancel</button>
      <button className="btn bp" onClick={saveConf} disabled={saving}>{saving?<><Ico name="loader" size={14} style={{animation:"spin 1s linear infinite"}}/>Saving…</>:<><Ico name="save" size={14}/>{form.id?"Save Changes":"Create Conference"}</>}</button>
    </div>
  </div>
</div>
  );
}


function SubmissionsPanel({ token, notify, setConfirm }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [filter, setFilter]           = useState("all"); // all | unread | read

  const fetchSubs = async () => {
    setLoading(true);
    try { const d = await api.getSubmissions(token); setSubmissions(d); }
    catch(e) { notify("Failed to load submissions: "+e.message, "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSubs(); }, []);

  const markRead = async (item) => {
    if (item.status === "read") return;
    try {
      await api.markSubmission(item.id, "read", token);
      setSubmissions(s => s.map(x => x.id===item.id ? {...x,status:"read"} : x));
    } catch(e) {}
  };

  const openItem = (item) => {
    setSelected(item);
    markRead(item);
  };

  const doDelete = (id) => setConfirm({ type:"danger",icon:"trash",title:"Delete Submission",msg:"Delete this submission permanently?",detail:"This message will be removed from the admin panel.",confirmLabel:"Delete",onOk: async () => {
    try {
      await api.deleteSubmission(id, token);
      setSubmissions(s => s.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
      notify("Submission deleted");
    } catch(e) { notify("Delete failed: "+e.message, "error"); }
    setConfirm(null);
  }});

  const fmt2 = d => d ? new Date(d).toLocaleString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—";

  const filtered = submissions.filter(s => filter==="all" ? true : s.status===filter);
  const unreadCount = submissions.filter(s => s.status==="unread").length;

  return (
    <div className="fu">
      <div className="pt">Contact Submissions</div>
      <div className="ps">Messages sent through the Contact Us form on the website</div>

      {/* Toolbar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:6}}>
          {[["all","All"],["unread","Unread"],["read","Read"]].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)}
              style={{padding:"5px 14px",borderRadius:6,border:"1px solid var(--bd)",fontSize:12,fontWeight:600,cursor:"pointer",
                background:filter===v?"var(--ac)":"var(--s2)",color:filter===v?"#dce8f5":"var(--t3)"}}>
              {l}{v==="unread"&&unreadCount>0?` (${unreadCount})`:""}
            </button>
          ))}
        </div>
        <button className="btn bse" onClick={fetchSubs}><Ico name="loader" size={13}/>Refresh</button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"340px 1fr",gap:16,minHeight:400}}>
        {/* List */}
        <div style={{border:"1px solid var(--bd)",borderRadius:8,overflow:"hidden",background:"#fff"}}>
          {loading ? (
            <div style={{padding:40,textAlign:"center",color:"var(--t3)"}}>Loading…</div>
          ) : filtered.length===0 ? (
            <div style={{padding:40,textAlign:"center",color:"var(--t3)"}}>No submissions yet.</div>
          ) : filtered.map(s=>(
            <div key={s.id} onClick={()=>openItem(s)}
              style={{padding:"12px 16px",borderBottom:"1px solid var(--bd)",cursor:"pointer",
                background:selected?.id===s.id?"#eff6ff":s.status==="unread"?"#fafbff":"#fff",
                borderLeft:s.status==="unread"?"3px solid #2563eb":"3px solid transparent",
                transition:"background .15s"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                <span style={{fontSize:13,fontWeight:s.status==="unread"?700:500,color:"#111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{s.name}</span>
                <span style={{fontSize:11,color:"var(--t4)",whiteSpace:"nowrap",marginLeft:8}}>{fmt2(s.created_at).split(",")[0]}</span>
              </div>
              <div style={{fontSize:12,color:"var(--t2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.subject}</div>
              <div style={{fontSize:11,color:"var(--t3)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.email}</div>
            </div>
          ))}
        </div>

        {/* Detail */}
        {selected ? (
          <div style={{border:"1px solid var(--bd)",borderRadius:8,background:"#fff",padding:24,display:"flex",flexDirection:"column",gap:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:18,fontWeight:700,color:"#111",marginBottom:4}}>{selected.subject}</div>
                <div style={{fontSize:13,color:"var(--t3)"}}>From <strong>{selected.name}</strong> · <a href={`mailto:${selected.email}`} style={{color:"var(--ac)"}}>{selected.email}</a></div>
                <div style={{fontSize:11,color:"var(--t4)",marginTop:4}}>{fmt2(selected.created_at)}</div>
              </div>
              <div style={{display:"flex",gap:8,flexShrink:0}}>
                <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                   style={{padding:"7px 14px",background:"var(--ac)",color:"#fff",borderRadius:6,fontSize:12,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
                  <Ico name="mail" size={13}/>Reply
                </a>
                <button onClick={()=>doDelete(selected.id)}
                  style={{padding:"7px 12px",background:"#fff",border:"1px solid #fca5a5",color:"#ef4444",borderRadius:6,fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                  <Ico name="trash" size={13}/>Delete
                </button>
              </div>
            </div>
            <div style={{background:"#f8fafc",borderRadius:8,padding:20,flex:1}}>
              <p style={{fontSize:14,color:"#334155",lineHeight:1.8,margin:0,whiteSpace:"pre-wrap"}}>{selected.message}</p>
            </div>
            {selected.ip && <div style={{fontSize:11,color:"#cbd5e1"}}>Sent from IP: {selected.ip}</div>}
          </div>
        ) : (
          <div style={{border:"1px solid var(--bd)",borderRadius:8,background:"#f8fafc",display:"flex",alignItems:"center",justifyContent:"center",color:"#94a3b8",fontSize:14}}>
            Select a message to read
          </div>
        )}
      </div>
    </div>
  );
}


/* ── MATH CAPTCHA GENERATOR ─────────────────────────────────── */
function genCaptcha() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const ops = [
    { q: `${a} + ${b}`, a: a + b },
    { q: `${a + b} - ${b}`, a: a },
    { q: `${a} × ${b}`, a: a * b },
  ];
  return ops[Math.floor(Math.random() * ops.length)];
}

/* ── DARK MODE HOOK ─────────────────────────────────────────── */
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const stored = localStorage.getItem('qd-admin-dark');
      if (stored !== null) return stored === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });
  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('qd-admin-dark', dark); } catch {}
  }, [dark]);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = e => { if (localStorage.getItem('qd-admin-dark') === null) setDark(e.matches); };
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return [dark, setDark];
}

export default function AdminApp() {
  const [dark, setDark] = useDarkMode();
  const [token,setToken]         = useState(getToken());
  const [section,setSection]     = useState("dashboard");
  const [articles,setArticles]   = useState([]);
  const [confs,setConfs]         = useState([]);
  const [loading,setLoading]     = useState({a:false,c:false});
  const [modal,setModal]         = useState(null);
  const [form,setForm]           = useState({});
  const [saving,setSaving]       = useState(false);
  const [toast,setToast]         = useState(null);
  const [confirm,setConfirm]     = useState(null);
  const [loginForm,setLoginForm] = useState({username:"",password:""});
  const [loginErr,setLoginErr]   = useState("");
  const [loginLoading,setLoginLoading] = useState(false);
  const [captcha,setCaptcha]     = useState(()=>genCaptcha());
  const [captchaVal,setCaptchaVal] = useState("");
  const [siteForm,setSiteForm]   = useState({});
  const [savingSettings,setSavingSettings] = useState(false);
  const [subscribers,setSubscribers] = useState([]);
  const [loadingSubs,setLoadingSubs] = useState(false);
  const [menus,setMenus]             = useState(null);
  const [savingMenus,setSavingMenus] = useState(false);
  const [siteImages,setSiteImages]   = useState({});
  const [savingImages,setSavingImages] = useState(false);
  const authed = !!token;

  const notify=useCallback((msg,type="success")=>{
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  },[]);

  const fetchA=useCallback(async()=>{
    setLoading(l=>({...l,a:true}));
    try{ setArticles(await api.getArticles(token)); }
    catch(e){ notify("Failed to load articles: "+e.message,"error"); }
    finally{ setLoading(l=>({...l,a:false})); }
  },[notify]);

  const fetchC=useCallback(async()=>{
    setLoading(l=>({...l,c:true}));
    try{ setConfs(await api.getConferences()); }
    catch(e){ notify("Failed to load conferences: "+e.message,"error"); }
    finally{ setLoading(l=>({...l,c:false})); }
  },[notify]);

  const fetchSettings=useCallback(async()=>{
    try{ const s=await api.getSettings(token); setSiteForm(s); }
    catch(e){ notify("Failed to load settings","error"); }
  },[notify,token]);

  const fetchSubscribers=useCallback(async()=>{
    setLoadingSubs(true);
    try{ setSubscribers(await api.getSubscribers(token)); }
    catch(e){ notify("Failed to load subscribers: "+e.message,"error"); }
    finally{ setLoadingSubs(false); }
  },[notify,token]);

  const fetchMenus=useCallback(async()=>{
    try{ setMenus(await api.getMenus()); }
    catch(e){ notify("Failed to load menus","error"); }
  },[notify]);

  const fetchImages=useCallback(async()=>{
    try{ setSiteImages(await api.getImages(token)); }
    catch(e){ console.warn("Could not load site images"); }
  },[notify,token]);

  useEffect(()=>{
    if(!authed) return;
    fetchA(); fetchC(); fetchSettings(); fetchSubscribers(); fetchMenus(); fetchImages();
  },[authed,fetchA,fetchC,fetchSettings,fetchSubscribers,fetchMenus,fetchImages]);

  useEffect(()=>{
    document.title=authed?"Admin Panel | Quantum Day":"Admin Login | Quantum Day";
  },[authed]);

  const doLogin=async e=>{
    e.preventDefault();
    if(!loginForm.username||!loginForm.password) return setLoginErr("Please fill in all fields.");
    if(parseInt(captchaVal) !== captcha.a) {
      setLoginErr("Incorrect answer to the security question. Please try again.");
      setCaptcha(genCaptcha()); setCaptchaVal("");
      return;
    }
    setLoginLoading(true); setLoginErr("");
    try{
      const t=await api.login(loginForm.username,loginForm.password);
      saveToken(t); setToken(t);
    } catch(e){ setLoginErr(e.message); }
    finally{ setLoginLoading(false); }
  };
  const doLogout=()=>setConfirm({
    type:"warning",icon:"logout",title:"Sign Out",
    msg:"Sign out of the admin panel?",
    detail:"Your session will be ended.",
    confirmLabel:"Sign Out",
    onOk:()=>{ saveToken(""); setToken(""); setLoginForm({username:"",password:""}); setConfirm(null); }
  });

  const openArt=item=>{
    setForm(item?{...item}:{id:null,category:"Research",tag:"",title:"",excerpt:"",content:"",author:"",author_role:"",read_time:"",featured:false,image:"atom",published_at:new Date().toISOString().slice(0,10),status:"published"});
    setModal({type:"article"});
  };
  const saveArt=async(confirmed=false)=>{
    if(!form.title?.trim()) return notify("Title is required","error");
    if(!form.author?.trim()) return notify("Author is required","error");
    // Auto-fill read_time if empty
    if(!form.read_time?.trim()) {
      const auto=calcReadTime(form.content,form.excerpt);
      if(auto) setForm(p=>({...p,read_time:auto}));
      form={...form,read_time:auto};
    }
    if(form.id && !confirmed){
      return setConfirm({
        type:"warning",icon:"edit",title:"Save Changes",
        msg:`Update "${form.title}"?`,
        detail:form.status==="draft"?"This article will remain as a draft and will not be visible to the public.":form.status==="archived"?"This article will be archived and hidden from the public.":"Changes will be immediately visible on the live website.",
        confirmLabel:"Save Changes",
        onOk:()=>{ setConfirm(null); saveArt(true); }
      });
    }
    setSaving(true);
    try{
      await api.saveArticle(form,token);
      await fetchA();
      setModal(null);
      const statusLabel = form.status==="draft"?" (saved as draft)":form.status==="archived"?" (archived)":"";
      notify(form.id?`Article updated${statusLabel}`:`Article created${statusLabel}`);
    }
    catch(e){ notify("Save failed: "+e.message,"error"); }
    finally{ setSaving(false); }
  };
  const delArt=(id,title)=>setConfirm({
    type:"danger",icon:"trash",title:"Delete Article",
    msg:`Delete "${title||"this article"}"?`,
    detail:"This action cannot be undone. The article will be permanently removed.",
    confirmLabel:"Delete Article",
    onOk:async()=>{
      try{ await api.deleteArticle(id,token); await fetchA(); notify("Article deleted"); }
      catch(e){ notify("Delete failed: "+e.message,"error"); }
      setConfirm(null);
    }
  });

  const openConf=item=>{
    setForm(item?{...item,topics:Array.isArray(item.topics)?item.topics.join(", "):item.topics||""}:{id:null,name:"",scale:"Global",date_start:"",date_end:"",location:"",venue:"",attendees:"",topics:"",status:"upcoming",website:"",featured:false});
    setModal({type:"conference"});
  };
  const saveConf=async(confirmed=false)=>{
    if(!form.name?.trim()) return notify("Conference name is required","error");
    if(!form.date_start) return notify("Start date is required","error");
    if(form.id && !confirmed){
      return setConfirm({
        type:"warning",icon:"edit",title:"Save Changes",
        msg:`Update "${form.name}"?`,
        detail:form.status==="draft"?"This article will remain as a draft and will not be visible to the public.":form.status==="archived"?"This article will be archived and hidden from the public.":"Changes will be immediately visible on the live website.",
        confirmLabel:"Save Changes",
        onOk:()=>{ setConfirm(null); saveConf(true); }
      });
    }
    const payload={...form,topics:typeof form.topics==="string"?form.topics.split(",").map(t=>t.trim()).filter(Boolean):(form.topics||[])};
    setSaving(true);
    try{ await api.saveConference(payload,token); await fetchC(); setModal(null); notify(form.id?"Conference updated":"Conference created"); }
    catch(e){ notify("Save failed: "+e.message,"error"); }
    finally{ setSaving(false); }
  };
  const delConf=(id,name)=>setConfirm({
    type:"danger",icon:"trash",title:"Delete Conference",
    msg:`Delete "${name||"this conference"}"?`,
    detail:"This action cannot be undone.",
    confirmLabel:"Delete Conference",
    onOk:async()=>{
      try{ await api.deleteConference(id,token); await fetchC(); notify("Conference deleted"); }
      catch(e){ notify("Delete failed: "+e.message,"error"); }
      setConfirm(null);
    }
  });

  const saveAllSettings=(confirmed=false)=>{
    if(!confirmed) return setConfirm({
      type:"warning",icon:"settings",title:"Save Settings",
      msg:"Apply these settings to the live website?",
      detail:"SEO metadata and site information will update immediately.",
      confirmLabel:"Save Settings",
      onOk:()=>{ setConfirm(null); saveAllSettings(true); }
    });
    setSavingSettings(true);
    api.saveSettings(siteForm,token)
      .then(()=>notify("Settings saved — changes live on main site"))
      .catch(e=>notify("Save failed: "+e.message,"error"))
      .finally(()=>setSavingSettings(false));
  };

  const S=`
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;background:#f5f4f2;color:#2c2c2e;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",Arial,sans-serif;-webkit-font-smoothing:antialiased;}
::-webkit-scrollbar{width:5px;}::-webkit-scrollbar-thumb{background:rgba(60,60,67,.18);border-radius:3px;}
:root{
  --bg:#f5f4f2;--bg2:#edebe8;--bg3:#e6e3de;
  --s:#fafaf8;--s2:#ffffff;
  --bd:rgba(60,60,67,.12);--bd2:rgba(60,60,67,.07);
  --t:#1c1c1e;--t2:#48484a;--t3:#8e8e93;--t4:#aeaeb2;
  --ac:#3a5f8a;--ach:#2d4f76;--acl:rgba(58,95,138,.1);--acl2:rgba(58,95,138,.16);
  --ok:#34863a;--okb:rgba(52,134,58,.09);--okbd:rgba(52,134,58,.22);
  --err:#c0392b;--errb:rgba(192,57,43,.07);--errbd:rgba(192,57,43,.2);
  --warn:#b7770d;--warnb:rgba(183,119,13,.09);--warnbd:rgba(183,119,13,.2);
  --wm:#7d5a35;--wmb:rgba(125,90,53,.09);
  --sb:232px;--tb:54px;
  --r:10px;--r2:7px;
  --sh:0 1px 3px rgba(0,0,0,.06),0 1px 2px rgba(0,0,0,.04);
  --sh2:0 4px 16px rgba(0,0,0,.08),0 1px 4px rgba(0,0,0,.04);
}

/* ── DARK MODE ──────────────────────────────────────────────── */
body.dark{
  --bg:#0f0f10;--bg2:#1a1a1c;--bg3:#222224;
  --s:#161618;--s2:#1c1c1e;
  --bd:rgba(255,255,255,.1);--bd2:rgba(255,255,255,.06);
  --t:#f2f2f7;--t2:#ebebf0;--t3:#98989d;--t4:#636366;
  --acl:rgba(58,95,138,.2);--acl2:rgba(58,95,138,.28);
  --okb:rgba(52,134,58,.15);--okbd:rgba(52,134,58,.3);
  --errb:rgba(192,57,43,.12);--errbd:rgba(192,57,43,.3);
  --warnb:rgba(183,119,13,.12);--warnbd:rgba(183,119,13,.3);
  --sh:0 1px 3px rgba(0,0,0,.3);
  --sh2:0 4px 16px rgba(0,0,0,.5);
}
body.dark .sb{background:#111113;border-right-color:rgba(255,255,255,.08);}
body.dark .sb-logo{border-bottom-color:rgba(255,255,255,.08);}
body.dark .ni:hover{background:rgba(255,255,255,.06);}
body.dark .ni.on{background:rgba(58,95,138,.25);color:var(--ac);}
body.dark .tb{background:#111113;border-bottom-color:rgba(255,255,255,.08);}
body.dark .main{background:var(--bg);}
body.dark .content{background:var(--bg);}
body.dark .tc{background:var(--s2);border-color:var(--bd);}
body.dark .sc{background:var(--s2);border-color:var(--bd);}
body.dark .mo{background:var(--s2);border-color:var(--bd);}
body.dark th{background:var(--bg2);border-bottom-color:var(--bd);}
body.dark td{border-bottom-color:var(--bd2);}
body.dark .fc2{background:var(--bg2);border-color:var(--bd);color:var(--t);}
body.dark .fc2:focus{background:var(--s2);}
body.dark .an-card{background:var(--s2);border-color:var(--bd);}
body.dark .an-box{background:var(--s2);border-color:var(--bd);}
body.dark .an-tab.on{background:var(--s2);}
body.dark .nb-status{background:var(--bg2);border-color:var(--bd);}
body.dark .nb-card{background:var(--s2);border-color:var(--bd);}
body.dark .nb-sched{background:var(--bg2);border-color:var(--bd);}
body.dark .nb-log{background:var(--bg2);border-color:var(--bd);color:var(--t3);}
body.dark .vsb{background:var(--bg2);border-color:var(--bd);}
body.dark .status-toggle{background:var(--bg2);}
body.dark .status-toggle button{background:var(--bg2);color:var(--t3);}
body.dark .lc{background:var(--s2);}
body.dark .lp{background:linear-gradient(160deg,#060c14 0%,#0d1f33 55%,#050a12 100%);}
body.dark input[type="number"]::-webkit-inner-spin-button{opacity:.4;}

/* ── DARK MODE TOGGLE BUTTON ─────────────────────────────────── */
.dm-btn{
  display:flex;align-items:center;justify-content:center;
  width:32px;height:32px;
  background:var(--bg);border:1px solid var(--bd);
  border-radius:7px;cursor:pointer;color:var(--t3);
  transition:all .18s;flex-shrink:0;
}
.dm-btn:hover{background:var(--bg2);color:var(--t);border-color:var(--ac);}
body.dark .dm-btn{background:var(--bg2);color:var(--t3);}
body.dark .dm-btn:hover{background:var(--bg3);color:var(--t);}

@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.25}}
@keyframes pop{0%{transform:scale(.94);opacity:0}100%{transform:scale(1);opacity:1}}
.fu{animation:fadeUp .28s ease both;}

/* ── LOGIN ── */
.lp{min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(160deg,#0d1f33 0%,#1a3a5c 55%,#0a1828 100%);padding:20px;}
.lc{background:var(--s2);border-radius:16px;padding:40px;width:100%;max-width:388px;box-shadow:0 24px 64px rgba(0,0,0,.22);}
.ll{display:flex;align-items:center;gap:10px;margin-bottom:26px;}
.lli{width:34px;height:34px;background:var(--ac);border-radius:8px;display:flex;align-items:center;justify-content:center;color:#dce8f5;}
.llt{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:17px;font-weight:700;color:var(--ac);letter-spacing:-.2px;}
.llb{font-size:10px;font-weight:600;letter-spacing:.8px;text-transform:uppercase;color:var(--t3);background:var(--bg2);padding:2px 8px;border-radius:20px;margin-left:4px;}
.ltitle{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:22px;font-weight:700;color:var(--t);margin-bottom:3px;letter-spacing:-.3px;}
.lsub{font-size:13px;color:var(--t3);margin-bottom:26px;line-height:1.5;}
.lerr{background:var(--errb);border:1px solid var(--errbd);border-radius:8px;padding:10px 13px;font-size:13px;color:var(--err);margin-bottom:16px;display:flex;align-items:center;gap:7px;}
.lform{display:flex;flex-direction:column;gap:14px;}
.lsubmit{width:100%;padding:11px;background:var(--ac);color:#dce8f5;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;transition:background .18s;display:flex;align-items:center;justify-content:center;gap:8px;letter-spacing:-.1px;}
.lsubmit:hover:not(:disabled){background:var(--ach);}
.lsubmit:disabled{opacity:.65;cursor:not-allowed;}
.lsec{display:flex;align-items:center;gap:5px;justify-content:center;margin-top:12px;font-size:11px;color:var(--t4);}

/* ── LAYOUT ── */
.layout{display:flex;min-height:100vh;}
.sb{width:var(--sb);background:var(--s);border-right:1px solid var(--bd);position:fixed;top:0;left:0;bottom:0;display:flex;flex-direction:column;z-index:100;}
.sb-logo{padding:16px 18px;border-bottom:1px solid var(--bd);display:flex;align-items:center;gap:9px;}
.sb-li{width:26px;height:26px;background:var(--ac);border-radius:6px;display:flex;align-items:center;justify-content:center;color:#dce8f5;flex-shrink:0;}
.sb-lt{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:14px;font-weight:700;color:var(--ac);letter-spacing:-.2px;line-height:1.2;}
.sb-ls{font-size:10px;color:var(--t4);margin-top:1px;letter-spacing:.2px;}
.sb-nav{flex:1;padding:12px 10px;display:flex;flex-direction:column;gap:1px;overflow-y:auto;}
.ni{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:var(--r2);font-size:13px;font-weight:450;color:var(--t3);cursor:pointer;transition:all .15s;user-select:none;letter-spacing:-.1px;}
.ni:hover{background:var(--bg2);color:var(--t2);}
.ni.on{background:var(--acl2);color:var(--ac);font-weight:600;}
.ni svg{opacity:.55;flex-shrink:0;}.ni.on svg{opacity:1;}
.ni:hover svg{opacity:.8;}
.nsep{height:1px;background:var(--bd);margin:6px 2px;}
.nsec{font-size:10px;font-weight:600;letter-spacing:.9px;text-transform:uppercase;color:var(--t4);padding:8px 10px 3px;}
.sb-foot{padding:10px;border-top:1px solid var(--bd);}
.sb-user{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:var(--r2);background:var(--bg);margin-bottom:4px;}
.su-av{width:26px;height:26px;border-radius:50%;background:var(--acl2);display:flex;align-items:center;justify-content:center;color:var(--ac);flex-shrink:0;}
.su-nm{font-size:13px;font-weight:600;color:var(--t);letter-spacing:-.1px;}
.su-rl{font-size:11px;color:var(--t4);}
.lout{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:var(--r2);font-size:13px;font-weight:450;color:var(--t3);cursor:pointer;transition:all .15s;}
.lout:hover{background:var(--errb);color:var(--err);}

/* ── TOPBAR ── */
.main{margin-left:var(--sb);flex:1;display:flex;flex-direction:column;min-height:100vh;}
.tb{height:var(--tb);background:var(--s);border-bottom:1px solid var(--bd);padding:0 24px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50;}
.bc{font-size:12.5px;color:var(--t4);display:flex;align-items:center;gap:5px;}
.bca{color:var(--t);font-weight:600;letter-spacing:-.1px;}
.vsb{display:flex;align-items:center;gap:6px;padding:6px 13px;background:var(--bg);border:1px solid var(--bd);border-radius:7px;font-size:12px;font-weight:500;color:var(--t3);text-decoration:none;transition:all .15s;}
.vsb:hover{color:var(--ac);border-color:var(--ac);}
.vsb .rdb{width:5px;height:5px;border-radius:50%;background:#30b050;display:inline-block;animation:blink 2.5s infinite;}

/* ── CONTENT ── */
.content{padding:24px 28px;flex:1;}
.pt{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:24px;font-weight:700;color:var(--t);letter-spacing:-.5px;margin-bottom:3px;}
.ps{font-size:13px;color:var(--t3);margin-bottom:24px;line-height:1.5;}

/* ── STAT CARDS ── */
.sg{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px;margin-bottom:24px;}
.sc{background:var(--s2);border:1px solid var(--bd);border-radius:var(--r);padding:18px 18px 16px;box-shadow:var(--sh);transition:box-shadow .2s;}
.sc:hover{box-shadow:var(--sh2);}
.si{width:32px;height:32px;border-radius:7px;background:var(--acl);display:flex;align-items:center;justify-content:center;color:var(--ac);margin-bottom:10px;}
.sn{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:30px;font-weight:700;color:var(--ac);line-height:1;letter-spacing:-.5px;}
.sl{font-size:11.5px;color:var(--t3);margin-top:4px;letter-spacing:-.1px;}

/* ── TABLES ── */
.tc{background:var(--s2);border:1px solid var(--bd);border-radius:var(--r);overflow:hidden;box-shadow:var(--sh);}
.th{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--bd);}
.tht{font-size:14px;font-weight:600;color:var(--t);letter-spacing:-.15px;}
table{width:100%;border-collapse:collapse;}
th{padding:10px 18px;text-align:left;font-size:10.5px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:var(--t4);border-bottom:1px solid var(--bd);background:var(--bg);}
td{padding:12px 18px;font-size:13px;color:var(--t2);border-bottom:1px solid var(--bd2);vertical-align:middle;}
tr:last-child td{border-bottom:none;}
tr:hover td{background:rgba(60,60,67,.02);}
.tdt{font-weight:500;color:var(--t);max-width:250px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;letter-spacing:-.1px;}
.tdt:hover{color:var(--ac);}

/* ── CHIPS ── */
.chip{display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:20px;font-size:11px;font-weight:500;}
.chip.cpub{background:rgba(52,134,58,.1);color:#2e7d32;}
.chip.cdraft{background:rgba(183,119,13,.1);color:#856404;}
.chip.carch{background:var(--bg2);color:var(--t4);}
.status-toggle{display:flex;gap:0;border:1px solid var(--bd);border-radius:8px;overflow:hidden;margin-top:4px;}
.status-toggle button{flex:1;padding:8px 0;font-size:12px;font-weight:600;border:none;cursor:pointer;transition:all .15s;background:var(--bg);color:var(--t3);}
.status-toggle button.on{color:#fff;}
.status-toggle button.on.spub{background:#2e7d32;}
.status-toggle button.on.sdraft{background:#856404;}
.status-toggle button.on.sarch{background:var(--t4);}
.ccat{background:var(--acl);color:var(--ac);}
.ctag{background:var(--bg2);color:var(--t3);}
.cft{background:rgba(125,90,53,.09);color:var(--wm);}
.cok{background:var(--okb);color:var(--ok);}
.cgl{background:var(--acl);color:var(--ac);}
.cre{background:var(--wmb);color:var(--wm);}

/* ── BUTTONS ── */
.btn{display:inline-flex;align-items:center;gap:5px;padding:7px 14px;border-radius:var(--r2);font-size:13px;font-weight:500;cursor:pointer;border:1px solid;transition:all .15s;font-family:inherit;letter-spacing:-.1px;}
.bp{background:var(--ac);border-color:var(--ac);color:#dce8f5;}
.bp:hover:not(:disabled){background:var(--ach);}
.bp:disabled{opacity:.55;cursor:not-allowed;}
.bse{background:var(--s2);border-color:var(--bd);color:var(--t2);}
.bse:hover{background:var(--bg2);color:var(--t);}
.bd{background:var(--errb);border-color:var(--errbd);color:var(--err);}
.bd:hover{background:rgba(192,57,43,.13);}
.bsm{padding:5px 10px;font-size:11.5px;border-radius:6px;}
.bic{padding:5px;border-radius:6px;}
.ar{display:flex;gap:5px;align-items:center;}
.dg2{display:grid;grid-template-columns:1fr 1fr;gap:16px;}

/* ── ANALYTICS DASHBOARD ── */
.an-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px;}
@media(max-width:900px){.an-grid{grid-template-columns:repeat(2,1fr);}}
.an-card{background:var(--s2);border:1px solid var(--bd);border-radius:12px;padding:18px 20px;position:relative;overflow:hidden;transition:box-shadow .2s;}
.an-card:hover{box-shadow:var(--sh2);}
.an-card-accent{position:absolute;top:0;left:0;right:0;height:3px;border-radius:12px 12px 0 0;}
.an-card-label{font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:var(--t4);margin-bottom:8px;}
.an-card-val{font-size:32px;font-weight:800;color:var(--t);letter-spacing:-1px;line-height:1;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif;}
.an-card-sub{font-size:11.5px;color:var(--t4);margin-top:5px;}
.an-card-trend{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:600;padding:2px 7px;border-radius:20px;margin-top:6px;}
.an-card-trend.up{background:rgba(52,134,58,.1);color:#2e7d32;}
.an-card-trend.dn{background:rgba(192,57,43,.08);color:#c0392b;}
.an-card-trend.nt{background:var(--bg2);color:var(--t4);}
/* Bar chart */
.an-bar-wrap{display:flex;flex-direction:column;gap:7px;margin-top:4px;}
.an-bar-row{display:flex;align-items:center;gap:10px;}
.an-bar-label{font-size:12px;color:var(--t2);width:130px;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;}
.an-bar-label:hover{color:var(--ac);}
.an-bar-track{flex:1;height:8px;background:var(--bg2);border-radius:4px;overflow:hidden;}
.an-bar-fill{height:100%;border-radius:4px;transition:width .6s cubic-bezier(.4,0,.2,1);}
.an-bar-val{font-size:11.5px;font-weight:600;color:var(--t3);width:44px;text-align:right;flex-shrink:0;}
/* Sparkline (pure CSS bar chart for time series) */
.an-spark{display:flex;align-items:flex-end;gap:3px;height:48px;margin-top:8px;}
.an-spark-bar{flex:1;border-radius:2px 2px 0 0;min-height:2px;transition:height .4s ease;opacity:.75;}
.an-spark-bar:hover{opacity:1;}
/* Section layout */
.an-row{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px;}
.an-row.w3{grid-template-columns:2fr 1fr;}
.an-row.w1{grid-template-columns:1fr;}
@media(max-width:860px){.an-row,.an-row.w3{grid-template-columns:1fr;}}
.an-box{background:var(--s2);border:1px solid var(--bd);border-radius:12px;padding:20px 22px;box-shadow:var(--sh);}
.an-box-hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;}
.an-box-title{font-size:13.5px;font-weight:700;color:var(--t);letter-spacing:-.2px;}
.an-box-sub{font-size:11px;color:var(--t4);}
/* Category donut legend */
.an-legend{display:flex;flex-direction:column;gap:8px;margin-top:4px;}
.an-legend-row{display:flex;align-items:center;gap:8px;}
.an-legend-dot{width:9px;height:9px;border-radius:50%;flex-shrink:0;}
.an-legend-name{font-size:12.5px;color:var(--t2);flex:1;}
.an-legend-pct{font-size:12px;font-weight:600;color:var(--t3);}
.an-legend-count{font-size:11px;color:var(--t4);margin-left:4px;}
/* Period tabs */
.an-tabs{display:flex;gap:2px;background:var(--bg2);border-radius:8px;padding:3px;}
.an-tab{padding:4px 11px;border-radius:6px;font-size:11.5px;font-weight:500;color:var(--t3);cursor:pointer;border:none;background:none;transition:all .15s;font-family:inherit;}
.an-tab.on{background:var(--s2);color:var(--t);box-shadow:0 1px 3px rgba(0,0,0,.08);font-weight:600;}
/* Empty state */
.an-empty{text-align:center;padding:32px 16px;color:var(--t4);font-size:13px;}

/* ── MODALS ── */
.ov{position:fixed;inset:0;background:rgba(28,28,30,.38);z-index:300;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(6px);animation:fadeIn .2s ease;}
.mo{background:var(--s2);border:1px solid var(--bd);border-radius:14px;padding:26px;width:100%;max-width:680px;max-height:92vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,.16);animation:pop .2s ease;}
.moh{display:flex;align-items:center;justify-content:space-between;margin-bottom:22px;}
.mot{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:18px;font-weight:700;color:var(--t);letter-spacing:-.3px;}
.moc{background:var(--bg2);border:none;color:var(--t3);cursor:pointer;padding:5px;border-radius:6px;display:flex;align-items:center;transition:background .15s;}
.moc:hover{background:var(--bg3);color:var(--t);}
.mof{display:flex;gap:8px;justify-content:flex-end;margin-top:24px;padding-top:20px;border-top:1px solid var(--bd);}

/* ── CONFIRM DIALOG ── */
.cfb{background:var(--s2);border:1px solid var(--bd);border-radius:14px;padding:28px 28px 24px;width:100%;max-width:360px;text-align:center;box-shadow:0 24px 60px rgba(0,0,0,.16);animation:pop .2s ease;}
.cfi{width:44px;height:44px;border-radius:50%;background:var(--errb);color:var(--err);display:flex;align-items:center;justify-content:center;margin:0 auto 14px;}
.cft3{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Helvetica Neue",sans-serif;font-size:17px;font-weight:700;color:var(--t);margin-bottom:6px;letter-spacing:-.2px;}
.cfm{font-size:13.5px;color:var(--t2);margin-bottom:22px;line-height:1.6;}
.cfbtns{display:flex;gap:8px;justify-content:center;}

/* ── CUSTOM CONFIRM (non-delete) ── */
.cfb-info .cfi{background:var(--acl);color:var(--ac);}
.cfb-warn .cfi{background:var(--warnb);color:var(--warn);}
.cfb-ok .cfi{background:var(--okb);color:var(--ok);}

/* ── TOAST ── */
.toast{position:fixed;bottom:20px;right:20px;z-index:500;padding:11px 16px;border-radius:9px;font-size:13px;font-weight:500;display:flex;align-items:center;gap:8px;box-shadow:0 8px 24px rgba(0,0,0,.12);animation:fadeUp .28s ease;max-width:320px;letter-spacing:-.1px;}
.ts{background:var(--okb);border:1px solid var(--okbd);color:var(--ok);}
.te{background:var(--errb);border:1px solid var(--errbd);color:var(--err);}
.tw{background:var(--warnb);border:1px solid var(--warnbd);color:var(--warn);}

/* ── FORMS ── */
.fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.fgr{display:flex;flex-direction:column;gap:5px;}
.fgr.full{grid-column:1/-1;}
.fl{font-size:10.5px;font-weight:600;letter-spacing:.4px;text-transform:uppercase;color:var(--t3);}
.fc2{padding:9px 12px;background:var(--bg);border:1px solid var(--bd);border-radius:7px;color:var(--t);font-size:13.5px;outline:none;transition:all .18s;width:100%;font-family:inherit;}
.fc2:focus{border-color:rgba(58,95,138,.45);background:var(--s2);box-shadow:0 0 0 3px var(--acl);}
textarea.fc2{resize:vertical;min-height:88px;line-height:1.6;}
textarea.fc2.tall{min-height:150px;}
select.fc2{appearance:none;cursor:pointer;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238e8e93' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;padding-right:28px;}
.fchk{display:flex;align-items:center;gap:7px;cursor:pointer;user-select:none;font-size:13px;color:var(--t2);}
.fchk input[type=checkbox]{width:15px;height:15px;accent-color:var(--ac);cursor:pointer;}
.fhint{font-size:11px;color:var(--t4);line-height:1.4;}
/* ── NEWS BOT ── */
.nb-status{display:flex;align-items:center;gap:8px;padding:14px 18px;background:var(--bg2);border-radius:10px;border:1px solid var(--bd);margin-bottom:18px;}
.nb-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}
.nb-dot.on{background:#22c55e;box-shadow:0 0 0 3px rgba(34,197,94,.2);}
.nb-dot.off{background:var(--t4);}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:.4;}}
.nb-status-txt{font-size:13px;font-weight:600;color:var(--t);}
.nb-status-sub{font-size:11px;color:var(--t4);margin-left:auto;}
.nb-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px;}
.nb-card{background:var(--s2);border:1px solid var(--bd);border-radius:10px;padding:16px 18px;}
.nb-card-label{font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:var(--t4);margin-bottom:6px;}
.nb-card-val{font-size:26px;font-weight:800;color:var(--t);letter-spacing:-1px;line-height:1;}
.nb-card-sub{font-size:11px;color:var(--t4);margin-top:4px;}
.nb-log{background:var(--bg);border:1px solid var(--bd);border-radius:8px;padding:14px 16px;font-family:"SF Mono","Fira Code",monospace;font-size:11.5px;line-height:1.7;color:var(--t2);max-height:320px;overflow-y:auto;white-space:pre-wrap;word-break:break-word;}
.nb-log-empty{color:var(--t4);font-style:italic;}
.nb-sources{display:flex;flex-direction:column;gap:6px;}
.nb-source-row{display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg2);border-radius:7px;font-size:12.5px;}
.nb-source-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;flex-shrink:0;}
.nb-sched{display:flex;align-items:center;gap:10px;padding:12px 16px;background:var(--bg2);border-radius:8px;border:1px solid var(--bd);margin-bottom:12px;}
.nb-sched-label{font-size:12px;color:var(--t3);flex:1;}
.nb-sched-val{font-size:13px;font-weight:600;color:var(--t);font-family:"SF Mono","Fira Code",monospace;}

/* ── SETTINGS ── */
.setc{background:var(--s2);border:1px solid var(--bd);border-radius:var(--r);padding:24px;box-shadow:var(--sh);}
.sets{margin-bottom:28px;padding-bottom:24px;border-bottom:1px solid var(--bd);}
.sets:last-child{border-bottom:none;margin-bottom:0;}
.sett{font-size:14px;font-weight:600;color:var(--t);margin-bottom:3px;letter-spacing:-.15px;}
.setsub{font-size:12px;color:var(--t3);margin-bottom:16px;line-height:1.5;}
.dbinfo{background:var(--bg);border:1px solid var(--bd);border-radius:7px;padding:14px 16px;font-size:12.5px;color:var(--t2);line-height:1.8;}
.dbinfo code{font-family:"SF Mono","Menlo",monospace;background:var(--bg2);padding:1px 5px;border-radius:3px;font-size:11.5px;}
.dbbadge{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;background:var(--okb);border:1px solid var(--okbd);border-radius:20px;font-size:11.5px;font-weight:600;color:var(--ok);margin-bottom:10px;}

/* ── IMAGE UPLOADER ── */
.img-zone{border:2px dashed var(--bd);border-radius:9px;padding:22px;text-align:center;cursor:pointer;transition:all .2s;background:var(--bg);position:relative;}
.img-zone:hover,.img-zone.drag{border-color:var(--ac);background:var(--acl);}
.img-zone input[type=file]{position:absolute;inset:0;opacity:0;cursor:pointer;width:100%;height:100%;}
.img-zone-icon{color:var(--t4);margin-bottom:7px;display:flex;justify-content:center;}
.img-zone-text{font-size:13px;color:var(--t3);margin-bottom:3px;}
.img-zone-hint{font-size:11px;color:var(--t4);}
.img-preview{position:relative;border-radius:7px;overflow:hidden;margin-top:10px;}
.img-preview img{width:100%;max-height:200px;object-fit:cover;display:block;border-radius:7px;}
.img-preview-remove{position:absolute;top:7px;right:7px;background:rgba(0,0,0,.55);color:#fff;border:none;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:14px;}
.img-uploading{display:flex;align-items:center;gap:9px;padding:11px;background:var(--acl);border-radius:7px;margin-top:7px;font-size:13px;color:var(--ac);}
.img-url-display{display:flex;align-items:center;gap:7px;padding:7px 11px;background:var(--bg);border:1px solid var(--bd);border-radius:6px;margin-top:7px;font-size:11.5px;color:var(--t3);word-break:break-all;}
.img-url-display a{color:var(--ac);text-decoration:underline;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.img-slots{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:16px;}
.img-slot{border:1px solid var(--bd);border-radius:9px;overflow:hidden;}
.img-slot-head{padding:9px 13px;background:var(--bg);border-bottom:1px solid var(--bd);font-size:11.5px;font-weight:600;color:var(--t2);display:flex;align-items:center;justify-content:space-between;}
.img-slot-body{padding:13px;}
.img-slot-preview{width:100%;height:96px;object-fit:cover;border-radius:6px;margin-bottom:9px;display:block;}
.img-slot-empty{height:96px;background:var(--bg2);border-radius:6px;margin-bottom:9px;display:flex;align-items:center;justify-content:center;color:var(--t4);font-size:12px;}
`;

  if(!authed) return(<>
    <style>{S}</style>
    <div className="lp"><div className="lc fu">
      <div className="ll">
        <div className="lli"><Ico name="atom" size={18}/></div>
        <div><div className="llt">Quantum Day</div></div>
        <span className="llb">Admin</span>
      </div>
      <div className="ltitle">Welcome back</div>
      <div className="lsub">Sign in to manage your site content</div>
      {loginErr&&<div className="lerr"><Ico name="alert" size={15}/>{loginErr}</div>}
      <form className="lform" onSubmit={doLogin}>
        <div className="fgr"><label className="fl">Username</label>
          <input className="fc2" autoComplete="username" value={loginForm.username} onChange={e=>setLoginForm(p=>({...p,username:e.target.value}))}/></div>
        <div className="fgr"><label className="fl">Password</label>
          <input className="fc2" type="password" autoComplete="current-password" value={loginForm.password} onChange={e=>setLoginForm(p=>({...p,password:e.target.value}))}/></div>
        {/* Math Captcha */}
        <div className="fgr">
          <label className="fl">Security Check</label>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:1,background:"var(--bg2)",border:"1px solid var(--bd)",borderRadius:8,padding:"9px 14px",
              fontSize:15,fontWeight:700,color:"var(--t)",letterSpacing:1,textAlign:"center",fontFamily:"monospace",userSelect:"none"}}>
              What is {captcha.q} ?
            </div>
            <input className="fc2" type="number" placeholder="Answer" value={captchaVal}
              onChange={e=>setCaptchaVal(e.target.value)}
              style={{width:90,textAlign:"center",fontSize:16,fontWeight:700}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",marginTop:4}}>
            <button type="button" onClick={()=>{setCaptcha(genCaptcha());setCaptchaVal("");}}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"var(--t4)",display:"flex",alignItems:"center",gap:3,padding:0}}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              New question
            </button>
          </div>
        </div>
        <button type="submit" className="lsubmit" disabled={loginLoading||!captchaVal}>
          {loginLoading?<><Ico name="loader" size={15} style={{animation:"spin 1s linear infinite"}}/>Signing in…</>:"Sign In"}
        </button>
      </form>
      <div className="lsec"><Ico name="lock" size={11}/>Secured via server-side authentication</div>
    </div></div>
  </>);

  const secLabel={dashboard:"Dashboard",analytics:"Analytics",newsbot:"News Bot",articles:"Articles",conferences:"Conferences",menus:"Menu Manager",media:"Media & Images",newsletter:"Newsletter",subscribers:"Subscribers",submissions:"Contact Submissions",settings:"Settings"};

  // → DashboardPanel


  // → ArticlesPanel


  // → ConferencesPanel


  // ─── MENU MANAGER ────────────────────────────────────────────
  // → MenusPanel


  // → SubscribersPanel


  // → SettingsPanel


  // → ArtModal


  // → ConfModal


  // ── MEDIA MANAGER — now uses a ref for stability ─────────────────
  // This is rendered at module level to avoid remount bug
  const mediaSave=(imgs,confirmed=false)=>{
    if(!confirmed) return setConfirm({
      type:"warning",icon:"image",title:"Save Images",
      msg:"Save and apply these image changes to the live website?",
      confirmLabel:"Save Images",
      onOk:()=>{ setConfirm(null); mediaSave(imgs,true); }
    });
    api.saveImages(imgs,token)
      .then(()=>{ setSiteImages(imgs); notify("Images saved successfully"); })
      .catch(e=>notify("Save failed: "+e.message,"error"));
  };

  return(<>
    <style>{S}</style>
    {toast&&<div className={`toast ${toast.type==="success"?"ts":toast.type==="error"?"te":"tw"}`}><Ico name={toast.type==="success"?"check":"alert"} size={16}/>{toast.msg}</div>}
    {confirm&&<div className="ov"><div className={`cfb fu cfb-${confirm.type||"danger"}`}><div className="cfi"><Ico name={confirm.icon||"alert"} size={22}/></div><div className="cft3">{confirm.title||"Are you sure?"}</div><div className="cfm">{confirm.msg}{confirm.detail&&<><br/><span style={{fontSize:12,color:"var(--t3)",display:"block",marginTop:6}}>{confirm.detail}</span></>}</div><div className="cfbtns"><button className="btn bse" onClick={()=>setConfirm(null)}>Cancel</button><button className={`btn ${confirm.type==="danger"?"bd":confirm.type==="send"?"bp":"bse"}`} style={confirm.type==="warning"?{background:"var(--warnb)",borderColor:"var(--warnbd)",color:"var(--warn)"}:{}} onClick={confirm.onOk}>{confirm.confirmLabel||"Confirm"}</button></div></div></div>}
    {modal?.type==="article"&&<ArtModal form={form} setForm={setForm} setModal={setModal} saveArt={saveArt} saving={saving} token={token}/>}
    {modal?.type==="conference"&&<ConfModal form={form} setForm={setForm} setModal={setModal} saveConf={saveConf} saving={saving}/>}
    <div className="layout">
      <aside className="sb">
        <div className="sb-logo">
          <div className="sb-li"><Ico name="atom" size={16}/></div>
          <div><div className="sb-lt">Quantum Day</div><div className="sb-ls">Admin Panel</div></div>
        </div>
        <nav className="sb-nav">
          <div className="nsec">Navigation</div>
          {[["dashboard","dash","Dashboard"],["analytics","chart","Analytics"],["newsbot","bot","News Bot"],["articles","article","Articles"],["conferences","calendar","Conferences"],["menus","nav","Menus"],["media","image","Media & Images"],["newsletter","mail","Newsletter"],["subscribers","link","Subscribers"],["submissions","mail","Submissions"]].map(([k,ic,lbl])=>(
            <div key={k} className={`ni${section===k?" on":""}`} onClick={()=>setSection(k)}><Ico name={ic} size={16}/>{lbl}</div>
          ))}
          <div className="nsep"/>
          <div className="nsec">System</div>
          <div className={`ni${section==="settings"?" on":""}`} onClick={()=>setSection("settings")}><Ico name="settings" size={16}/>Settings</div>
        </nav>
        <div className="sb-foot">
          <div className="sb-user"><div className="su-av"><Ico name="shield" size={14}/></div><div><div className="su-nm">Administrator</div><div className="su-rl">admin</div></div></div>
          <div className="lout" onClick={doLogout}><Ico name="logout" size={15}/>Sign Out</div>
        </div>
      </aside>
      <div className="main">
        <div className="tb">
          <div><div className="bc"><span>Quantum Day</span><span>›</span><span className="bca">{secLabel[section]}</span></div></div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setDark(d=>!d)} title={dark?"Switch to Light Mode":"Switch to Dark Mode"} className="dm-btn">
              {dark
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              }
            </button>
            <a className="vsb" href="https://quantum-day.com" target="_blank" rel="noopener noreferrer"><Ico name="eye" size={13}/>View Site <span className="rdb"/></a>
          </div>
        </div>
        <div className="content">
          {section==="dashboard"&&<DashboardPanel articles={articles} confs={confs} subscribers={subscribers} loading={loading} setSection={setSection} openArt={openArt} openConf={openConf}/>}
          {section==="analytics"&&<AnalyticsPanel articles={articles} confs={confs} subscribers={subscribers} setSection={setSection} openArt={openArt}/>}
          {section==="newsbot"&&<NewsBotPanel token={token} notify={notify} setConfirm={setConfirm}/>}
          {section==="articles"&&<ArticlesPanel articles={articles} loading={loading} openArt={openArt} delArt={delArt} token={token} fetchA={fetchA} notify={notify} setConfirm={setConfirm}/>}
          {section==="conferences"&&<ConferencesPanel confs={confs} loading={loading} openConf={openConf} delConf={delConf}/>}
          {section==="menus"&&menus&&<MenusPanel menus={menus} setMenus={setMenus} token={token} notify={notify} fetchMenus={fetchMenus} setConfirm={setConfirm}/>}
          {section==="media"&&<MediaManager token={token} siteImages={siteImages} articles={articles} onSave={mediaSave} notify={notify} setSection={setSection}/>}
          {section==="newsletter"&&<NewsletterAdmin token={token} notify={notify}/>}
          {section==="subscribers"&&<SubscribersPanel subscribers={subscribers} loadingSubs={loadingSubs} fetchSubs={fetchSubscribers} token={token} notify={notify} setConfirm={setConfirm}/>}
          {section==="submissions"&&<SubmissionsPanel token={token} notify={notify} setConfirm={setConfirm}/>}
          {section==="settings"&&<SettingsPanel siteForm={siteForm} setSiteForm={setSiteForm} saveAllSettings={saveAllSettings} savingSettings={savingSettings}/>}
        </div>
      </div>
    </div>
  </>);
}
